import { Component, createRef } from 'preact';
import { TabBar } from './ui/TabBar';

const EDIT_PROFILE_TABS = [
  { key: 'avatar', label: k('Portrait') },
  { key: 'background', label: k('Background') },
  { key: 'title', label: k('Title') },
];
import RuneSpinner from './RuneSpinner';
import { CloseButton } from './ui/navButtons';
import { UI, playUI } from '../utils/arena/uiSounds';
import { getPublicProfile } from '../utils/friendsApi';
import { xpProgressInLevel, levelFromXp } from '../utils/arena/profileDefaults';
import { resolveAvatarUrl } from '../utils/arena/avatarUtils';
import { resolveProfileBackdrop } from '../utils/arena/profileBackdrop';
import { getHubProfileBg, setHubProfileBg } from '../utils/arena/hubBackground';
import ProfileHero, { HeroBackdrop } from './profile/ProfileHero';
import { extractAccentFromImage, accentPalette } from '../utils/arena/profileAccent';
import { ACHIEVEMENTS } from '../utils/arena/achievements';
import AchievementsPanel from './AchievementsPanel';
import ProfileTrophies from './ProfileTrophies';
import { getCanvasScale, onCanvasScaleChange } from '../utils/uiScale';
import { getCrownsForUser } from '../utils/arena/hallOfFameCache';
import { FOUNDER_TITLE_LABEL, FOUNDER_PICKER_SENTINEL } from '../utils/arena/founderTitle';
import { QA_TITLE_LABEL, QA_TITLE_EPITHET, QA_PICKER_SENTINEL } from '../utils/arena/qaTitle';
import { PIN_LIMIT, buildPlayerBadges, resolvePinnedBadges } from '../utils/arena/playerBadges';
import { getCosmeticsCached } from '../utils/arena/cosmeticsCache';
import { replayableMatches, applyMatchMetaPatch } from '../utils/arena/matchHistory';
import ReplayHistoryCard from './ReplayHistoryCard';
import { pushDismiss, removeDismiss } from '../utils/escStack';
import { isBasicSite } from '../utils/sorcery/basicSites';
import AvatarCrownBadge from './AvatarCrownBadge';
import DeckCardTile from './DeckCardTile';
import CardGrid from './CardGrid';
import { isFoilFinish, resolvePrinting } from '../utils/sorcery/foil';
import { foilZonesForCard } from '../utils/sorcery/foilZones';
import DeckTile, { DeckTileText } from './DeckTile';
import CardInspector from './CardInspector';
import { CoinIcon, ShardIcon, KeystoneIcon, IconSkillTree } from './ui/icons';
import { timeAgo } from '../utils/arena/timeFormat';
import { k, t } from '../localization/t';
import {
  TEXT_PRIMARY, TEXT_BODY, TEXT_MUTED,
  ACCENT, ACCENT_BRIGHT, ACCENT_HI, ACCENT_LO, ACCENT_DEEP, ACCENT_TEXT, ACCENT_FALLBACK, PEARL,
  TEXTURED_PANEL_STYLE, BEVELED_BTN, DANGER_BTN,
  VIGNETTE, COIN_COLOR, BG_ATMOSPHERE,
  CONTENT_BG_DEFAULT, PANEL_BG,
  FourCorners, OrnamentalDivider, KnotOrnament, RADIUS,
} from '../lib/medievalTheme';

// Every gold on this page (panel strokes, corner plating, headings,
// buttons, the avatar rim, the name) is built from the theme's
// ACCENT_* tokens, which read the `--vh-accent-*` custom properties
// set on the overlay root. Those hold the palette sampled from the
// player's background art (profileAccent.js), or the gold fallback
// until the art has been sampled or when it has no usable accent.

// Section-sized variant for the bigger left/right/center column
// panels (Treasury, Rank Progress, Collection, Recent Matches,
// Achievements, Decks/Showcase). Same texture + gold language as the
// small tablets so the visual family is consistent, but with reduced
// rim saturation and softer outer halo so a wall of six section
// panels doesn't fight for attention or overpower the small gilded
// chips inside them.
const STONE_SECTION_STYLE = {
  backdropFilter: 'blur(16px)',
  WebkitBackdropFilter: 'blur(16px)',
  background: `${CONTENT_BG_DEFAULT}, ${PANEL_BG}`,
  backgroundBlendMode: 'overlay, normal',
  border: `1px solid ${ACCENT} 0.42)`,
  borderRadius: 'var(--radius-lg)',
  boxShadow: [
    'inset 0 1px 0 rgba(255,235,195,0.08)',
    `inset 0 0 0 1px ${ACCENT} 0.14)`,
    'inset 0 -2px 6px rgba(0,0,0,0.45)',
    'inset 0 0 32px rgba(0,0,0,0.45)',
    '0 4px 14px rgba(0,0,0,0.45)',
    `0 0 24px ${ACCENT} 0.08)`,
  ].join(', '),
  isolation: 'isolate',
  transition: 'border-color 400ms ease, box-shadow 400ms ease',
};

// Secondary panels (Treasury, Collection, Recent Matches, Achievements)
// drop to a borderless matte recess so the hero and the deck rail
// carry the page. No hairline, no corner plating, no outer glow: the
// panel reads as a hollow carved into the stone rather than a box
// laid on top of it, which is what keeps the page from turning into a
// wall of identical frames.
const STONE_RECESS_STYLE = {
  backdropFilter: 'blur(16px)',
  WebkitBackdropFilter: 'blur(16px)',
  background: `${CONTENT_BG_DEFAULT}, linear-gradient(180deg, rgba(10,8,6,0.66) 0%, rgba(6,5,3,0.78) 100%)`,
  backgroundBlendMode: 'overlay, normal',
  border: `1px solid ${ACCENT} 0.42)`,
  borderRadius: 'var(--radius-lg)',
  boxShadow: [
    'inset 0 2px 10px rgba(0,0,0,0.6)',
    'inset 0 0 44px rgba(0,0,0,0.45)',
    `inset 0 -1px 0 ${ACCENT_HI} 0.05)`,
    `inset 0 0 0 1px ${ACCENT} 0.07)`,
  ].join(', '),
  isolation: 'isolate',
};

// Section banner. Centered label between horizontal gold rules.
// Same recipe the dice modal and achievement overlay use, so the
// profile reads as part of the same family of ceremonial views.
function SectionBanner({ children, count }) {
  return (
    <div className="flex items-center gap-3 w-full mb-2.5">
      <div className="flex-1 h-px" style={{ background: `linear-gradient(90deg, transparent 0%, ${ACCENT} 0.4) 100%)` }} />
      <span
        className="text-[0.5625cqw] font-semibold shrink-0"
        style={{ color: ACCENT_TEXT, textShadow: `0 0 8px ${ACCENT} 0.35)` }}
      >
        {children}
        {count != null ? (
          <span className="ml-1.5 tabular-nums" style={{ color: TEXT_MUTED }}>· {count}</span>
        ) : null}
      </span>
      <div className="flex-1 h-px" style={{ background: `linear-gradient(90deg, ${ACCENT} 0.4) 0%, transparent 100%)` }} />
    </div>
  );
}

// Max cards a player can pin to their profile showcase. Used as the
// hard cap in the picker, the slice limit on what the profile renders,
// and the empty-slot count in the grid. Kept as a single constant so
// the cap can be tuned in one place. Server-side mirror (`updateShowcaseCards`
// in app.jsx) and the schema comment should track this value.
const SHOWCASE_CAP = 8;

// CardGrid's headroom above the first row and below the last, in design px
// (1cqw). Just enough for a hovered card's lift and the plinth under it.
const SHOWCASE_VPAD = 16;

// The showcase never scrolls. The owner sees all eight slots in four
// columns and two rows; a visitor sees only the filled ones, so up to four
// cards stand in a single, larger row. Cards are sized from whichever of
// the panel's height or width binds first, and the grid's columns take
// whatever width they are handed, so the frame hands them exactly the
// width the rows fill and centres that box in the panel.
class ShowcaseFrame extends Component {
  state = { box: null };
  ref = createRef();

  componentDidMount() {
    this.ro = new ResizeObserver(() => this.fit());
    this.ro.observe(this.ref.current);
    this.fit();
  }

  componentDidUpdate(prev) {
    if (prev.canvasScale !== this.props.canvasScale || prev.cols !== this.props.cols || prev.rows !== this.props.rows) this.fit();
  }

  componentWillUnmount() {
    this.ro?.disconnect();
  }

  fit() {
    const el = this.ref.current;
    if (!el) return;
    const { cols, rows } = this.props;
    const s = this.props.canvasScale || 1;
    // Grid geometry at design scale: a 1.125cqw (18px) gap, px-3 gutters
    // (12px each) and SHOWCASE_VPAD above and below the rows.
    const gap = 18 * s;
    const padX = 2 * 12 * s;
    const padY = 2 * SHOWCASE_VPAD * s;
    const byHeight = (el.clientHeight - padY - (rows - 1) * gap) / rows;
    const byWidth = ((el.clientWidth - padX - (cols - 1) * gap) / cols) * (88 / 63);
    const tileH = Math.min(byHeight, byWidth);
    if (tileH <= 0) return;
    const box = {
      width: Math.floor(cols * tileH * (63 / 88) + (cols - 1) * gap + padX),
      height: Math.floor(rows * tileH + (rows - 1) * gap + padY),
    };
    if (box.width !== this.state.box?.width || box.height !== this.state.box?.height) this.setState({ box });
  }

  render() {
    const { box } = this.state;
    return (
      <div ref={this.ref} className="flex-1 min-h-0 flex items-center justify-center">
        <div className="flex flex-col" style={box ? { width: `${box.width}px`, height: `${box.height}px` } : { width: '100%', height: '100%' }}>
          {this.props.children}
        </div>
      </div>
    );
  }
}

// The card's own shadow on the floor, so it stands off the stone rather
// than reading as a thumbnail in a grid. Sits behind the tile and never
// takes a click.
function ShowcasePlinth() {
  return (
    <div
      aria-hidden="true"
      className="absolute pointer-events-none"
      style={{ inset: 0, zIndex: 0, borderRadius: 'var(--radius-xl)', boxShadow: '0 0.6cqw 1.4cqw rgba(0,0,0,0.75)' }}
    />
  );
}

const SET_COLORS = {
  gothic: `linear-gradient(90deg, rgba(168,120,50,0.5) 0%, rgba(168,120,50,0) 100%)`,
  arthurian: `linear-gradient(90deg, rgba(107,140,174,0.5) 0%, rgba(107,140,174,0) 100%)`,
  beta: `linear-gradient(90deg, rgba(124,110,160,0.5) 0%, rgba(124,110,160,0) 100%)`,
};

export default class FriendProfileOverlay extends Component {
  constructor(props) {
    super(props);
    this.state = {
      profile: null,
      canvasScale: getCanvasScale(),
      loading: true,
      error: null,
      showUnfriendConfirm: false,
      crowns: [],
      // Title preview index used by the Edit Profile title picker to
      // flash the headline under the player's name when the user
      // hovers a crown row. The header badge strip no longer drives
      // this. Clicking a badge there opens the Badges viewer and
      // never mutates the displayed title (that's Edit Profile's job).
      selectedCrownIndex: -1,
      // Badges viewer modal. Opens when the user clicks the header
      // badge strip. Read-only listing of every earned badge so future
      // non-crown badges (achievements, event rewards) can be slotted
      // in without changing the strip UI again.
      showBadgesModal: false,
      // Active tab in the right-rail showcase.
      //   'showcase' → player-pinned trophy cards (grid of card art)
      //   'decks'    → library-published decks (compact list)
      //   'trophies' → ranked-season podium finishes
      activeShowcaseTab: 'showcase',
      // Showcase replace modal (self-only). When set to a non-null
      // number, renders a card-picker for THAT specific showcase slot.
      // Clicking a card in the picker replaces the slot in-place and
      // closes the modal. No multi-select staging.
      replacingSlotIndex: null,
      showcasePickerSearch: '',
      // Showcase / published-deck card inspector. Mirrors the deck
      // builder pattern: space (or click) on a hovered card opens the
      // full-screen CardInspector; Escape or space again closes it.
      hoveredShowcaseEntry: null,
      inspectedShowcaseEntry: null,
      // Showcase edit mode (self only). When on, clicking any card.
      // filled or empty. Opens the picker so the player can swap it.
      // When off, clicking a card opens the inspector instead. The
      // toggle lives as an icon button in the showcase tab bar.
      showcaseEditMode: false,
      // Edit-profile modal (self only). Three sub-tabs: avatar /
      // background / title. Each tab gets its own search input so the
      // pickers stay usable as the catalog grows.
      editProfileOpen: false,
      editProfileTab: 'avatar', // 'avatar' | 'background' | 'title'
      editProfileSearch: '',
      // Whether the main menu uses the chosen profile background as
      // its backdrop. Backed by the shared hubBackground store, so
      // flipping it here repaints the hub live behind the overlay.
      hubBgOn: getHubProfileBg(),
      // True while the seamless close fade plays. The overlay keeps
      // rendering (with the fade-out class on its root) until the
      // timer in requestClose fires the real onClose.
      closing: false,
      // Cosmetics catalog snapshot ({ catalog, owned }). Lazily loaded
      // in componentDidMount via the cache module.
      cosmeticsCatalog: null,
      // `--vh-accent-*` palette sampled from the hero art, or null for gold.
      accent: null,
    };
    this._accentUrl = null;
  }

  componentDidMount() {
    this.unsubCanvasScale = onCanvasScaleChange((scale) => this.setState({ canvasScale: scale }));
    this.loadProfile();
    this.loadCrowns();
    this.loadCosmeticsCatalog();
    window.addEventListener('keydown', this.handleShowcaseKeyDown);
  }

  async loadCosmeticsCatalog() {
    try {
      const data = await getCosmeticsCached();
      this.setState({ cosmeticsCatalog: data });
    } catch (err) {
      // Best-effort. If the endpoint fails we fall back to showing
      // only the player's playable card collection in the picker.
      console.warn('[FriendProfileOverlay] Failed to load cosmetics:', err);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const open = !!this.state.inspectedShowcaseEntry;
    const wasOpen = !!prevState.inspectedShowcaseEntry;
    if (open && !wasOpen) this._escDismissId = pushDismiss(() => this.setState({ inspectedShowcaseEntry: null }));
    else if (!open && wasOpen) removeDismiss(this._escDismissId);
    if (prevState.activeShowcaseTab !== this.state.activeShowcaseTab) playUI(UI.TAB);
    this.syncAccent();
  }

  // Mouse parallax for the hero: two unit offsets written as custom
  // properties on the overlay root (no re-render). The banner and the
  // avatar plate translate off them in opposite directions.
  handleParallax = (e) => {
    if (this._parallaxFrame) return;
    const root = e.currentTarget;
    const { clientX, clientY } = e;
    this._parallaxFrame = requestAnimationFrame(() => {
      this._parallaxFrame = null;
      const rect = root.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      root.style.setProperty('--hero-px', (((clientX - rect.left) / rect.width - 0.5) * 2).toFixed(3));
      root.style.setProperty('--hero-py', (((clientY - rect.top) / rect.height - 0.5) * 2).toFixed(3));
    });
  };

  componentWillUnmount() {
    clearTimeout(this._closeTimer);
    if (this._parallaxFrame) cancelAnimationFrame(this._parallaxFrame);
    this.unsubCanvasScale?.();
    window.removeEventListener('keydown', this.handleShowcaseKeyDown);
    removeDismiss(this._escDismissId);
  }

  // Space-to-inspect mirrors the deck builder: pressing space while a
  // showcase card is hovered pops the full CardInspector; pressing it
  // again (or Escape) closes the inspector. Ignored when the player is
  // typing in an input. Same isEditableTarget guard the deck builder
  // uses.
  handleShowcaseKeyDown = (e) => {
    const tag = e.target?.tagName?.toLowerCase();
    if (e.target?.isContentEditable || tag === 'input' || tag === 'textarea' || tag === 'select') return;
    // Don't intercept space when the showcase picker modal is open.
    // that overlay owns its own search input and shouldn't have
    // space stolen for inspect.
    if (this.state.replacingSlotIndex != null) return;
    if (e.key === ' ' || e.code === 'Space') {
      if (e.repeat) return;
      const { hoveredShowcaseEntry, inspectedShowcaseEntry } = this.state;
      if (inspectedShowcaseEntry) {
        e.preventDefault();
        this.setState({ inspectedShowcaseEntry: null });
      } else if (hoveredShowcaseEntry) {
        e.preventDefault();
        this.setState({ inspectedShowcaseEntry: hoveredShowcaseEntry });
      }
      return;
    }
  };

  // The overlay holds its own copy of the profile, so a rename or pin has to
  // land here as well as in the app's arenaProfile (which owns the request).
  handleUpdateMatchMeta = (roomCode, patch) => {
    this.setState(({ profile }) => (profile
      ? { profile: { ...profile, matchHistory: applyMatchMetaPatch(profile.matchHistory, roomCode, patch) } }
      : null));
    this.props.onUpdateMatchMeta(roomCode, patch);
  };

  async loadProfile() {
    try {
      const profile = await getPublicProfile(this.props.profileId);
      // Merge live presence (online + auto-derived activity label) from
      // the friend list. The public profile endpoint snapshots the
      // player row but doesn't include WS-driven status. The friend
      // object's `customStatus` IS the activity label (auto-set by
      // their client on every screen change), so we read that directly.
      const presence = this.props.friendPresence;
      if (presence) {
        profile.online = presence.online ?? false;
        profile.customStatus = presence.customStatus ?? null;
      }
      this.setState({ profile, loading: false });
    } catch (err) {
      this.setState({ error: err.message, loading: false });
    }
  }

  async loadCrowns() {
    try {
      const crowns = await getCrownsForUser(this.props.profileId, this.props.sorceryCards);
      this.setState({ crowns });
    } catch (err) {
      console.warn('[FriendProfileOverlay] Failed to load crowns:', err);
    }
  }

  // Own profile with the menu-background toggle on: the hub is showing
  // this exact backdrop underneath the overlay, so open/close can be
  // staged as a crossfade over a continuous background.
  isSeamless() {
    return this.state.hubBgOn
      && !!this.props.currentUserId
      && this.props.profileId === this.props.currentUserId;
  }

  // Close the overlay. In the seamless case the root gets the fade-out
  // class first and the real onClose fires when the fade lands, so the
  // profile melts back into the menu instead of popping.
  requestClose = () => {
    if (this.state.closing) return;
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (!this.isSeamless() || reduceMotion) {
      this.props.onClose();
      return;
    }
    this.setState({ closing: true });
    this._closeTimer = setTimeout(() => this.props.onClose(), 300);
  };

  resolveHeroArt(profile) {
    // While our own profile fetch is in flight, fall back to the local
    // arenaProfile so the backdrop mounts immediately instead of the
    // page flashing its solid base colour. Same card id on both, so
    // the img src doesn't change when the fetched profile lands.
    const fallback = (!profile && this.props.profileId === this.props.currentUserId)
      ? this.props.selfProfile
      : null;
    return resolveProfileBackdrop(profile || fallback, this.props.sorceryCards);
  }

  // Re-sample the accent whenever the hero art changes. The last
  // requested URL is remembered so a superseded request resolving
  // late can't overwrite the current accent.
  syncAccent() {
    const url = this.resolveHeroArt(this.state.profile)?.url || null;
    if (url === this._accentUrl) return;
    this._accentUrl = url;
    if (!url) {
      this.setState({ accent: null });
      return;
    }
    extractAccentFromImage(url).then((c) => {
      if (this._accentUrl !== url) return;
      this.setState({ accent: c ? accentPalette(c) : null });
    });
  }

  render() {
    const { profile, loading, error } = this.state;
    const heroArt = this.resolveHeroArt(profile);
    // When the hub is already showing this same backdrop underneath
    // (own profile + menu-background toggle on), skip the art's
    // entrance animation and crossfade the whole overlay instead:
    // the shared art stays put while the menu content fades into the
    // profile content on open and back out on close.
    const hubShowsSameBackdrop = this.isSeamless();
    const fadeClass = hubShowsSameBackdrop
      ? (this.state.closing ? ' vh-profile-fade-out' : ' vh-profile-fade-in')
      : '';

    return (
      <div
        className={`fixed inset-0 z-[120] flex flex-col${fadeClass}`}
        onMouseMove={this.handleParallax}
        style={{
          // Solid fallback colour for when there's no avatar art yet
          // (loading state). Once the art mounts it fills the whole
          // page via the blurred backdrop layer below.
          background: '#080604',
          ...(this.state.accent || ACCENT_FALLBACK),
        }}
      >
        {/* Hero backdrop. Covers the ENTIRE page, not just the top.
            The chosen card art is blurred + tinted so the whole
            profile picks up that player's colour signature. A radial
            vignette darkens the edges so the content panels stay
            legible against any palette. */}
        <HeroBackdrop art={heroArt} animateIn={!hubShowsSameBackdrop} />
        <div className="fixed inset-0 pointer-events-none" style={{ background: VIGNETTE }} />

        <div className="relative flex-1 flex flex-col min-h-0">
          {loading ? (
            <div className="flex-1 flex items-center justify-center">
              <RuneSpinner size={64} />
            </div>
          ) : error ? (
            <div className="flex-1 flex flex-col items-center justify-center gap-4">
              <div className="text-sm" style={{ color: '#c45050' }}>{error}</div>
              <CloseButton onClick={this.requestClose} style={{ backgroundColor: 'rgb(20, 14, 6)' }} />
            </div>
          ) : profile ? this.renderProfile(profile) : null}
        </div>
      </div>
    );
  }

  // Open the replace-card picker for a specific showcase slot. The
  // picker stays single-purpose: pick one card from the player's
  // collection, drop it into `slotIndex`, save, close. No multi-
  // select / draft staging.
  openReplaceSlot = (slotIndex) => {
    this.setState({ replacingSlotIndex: slotIndex, showcasePickerSearch: '' });
  };

  closeReplaceSlot = () => this.setState({ replacingSlotIndex: null, showcasePickerSearch: '' });

  // Apply the replacement: build a fresh showcaseCards array with the
  // picked card swapped into the target slot, persist via the server
  // callback, and optimistically merge into local state so the
  // showcase re-renders without waiting for a profile refetch. When
  // `nextEntry` is null, the slot is emptied.
  applySlotReplacement = async (slotIndex, nextEntry) => {
    const { profile } = this.state;
    if (!profile || slotIndex == null) return;
    const current = Array.isArray(profile.showcaseCards) ? [...profile.showcaseCards] : [];
    // Pad with nulls up to the slot index so we can index into it
    // even when earlier slots are empty.
    while (current.length <= slotIndex) current.push(null);
    if (nextEntry) {
      current[slotIndex] = { cardId: nextEntry.cardId, foiling: nextEntry.foiling || 'S' };
    } else {
      current[slotIndex] = null;
    }
    // Strip trailing nulls so the saved array stays compact.
    const compact = current.filter((c) => c && c.cardId);
    this.closeReplaceSlot();
    try {
      await this.props.onSaveShowcase?.(compact);
      this.setState((s) => ({
        profile: s.profile ? { ...s.profile, showcaseCards: compact } : s.profile,
      }));
    } catch (err) {
      console.warn('[FriendProfileOverlay] Failed to save showcase replacement:', err);
    }
  };

  renderProfile(profile) {
    const { onClose, onRemoveFriend, onSendMail, onChallenge, profileId, isFriend, sorceryCards, currentUserId } = this.props;
    const { showUnfriendConfirm, crowns, selectedCrownIndex, showBadgesModal, activeShowcaseTab, replacingSlotIndex, showcasePickerSearch, showcaseEditMode, editProfileOpen, editProfileTab, editProfileSearch } = this.state;
    // Self-view is determined by id match against the current
    // authenticated user. When self, the showcase tab renders empty
    // "+ Add Card" slots and an Edit button; for others, only filled
    // slots render.
    const isSelf = !!currentUserId && currentUserId === profile.id;
    // Headline crown follows the tri-state used everywhere titles
    // appear: null defaults to auto-pick (highest-wins), '__none__'
    // means explicitly hidden, and any other value is the player's
    // explicit pick. The header badge strip is display-only; the
    // displayed crown is only changed via the Edit Profile → Title
    // tab so there's a single canonical way to swap titles.
    const chosenAvatarId = profile.displayedCrownAvatarId || null;
    let chosenCrown = null;
    // Founder title is rendered as a crown-shaped object here so the
    // headline reuses the same `displayedCrown.title` rendering path.
    // `avatarId` is the founder sentinel; the badge in the strip is
    // hidden for it (we only render the title under the name).
    const founderCrown = profile.isFounder ? {
      avatarId: FOUNDER_PICKER_SENTINEL,
      label: FOUNDER_TITLE_LABEL,
      title: FOUNDER_TITLE_LABEL,
      founder: true,
    } : null;
    // Same shape for the QA title, except label and epithet differ.
    // the headline renders `title`, so it gets the epithet.
    const qaCrown = profile.isQaTester ? {
      avatarId: QA_PICKER_SENTINEL,
      label: QA_TITLE_LABEL,
      title: QA_TITLE_EPITHET,
      qa: true,
    } : null;
    if (chosenAvatarId === '__none__') {
      chosenCrown = null;
    } else if (chosenAvatarId === FOUNDER_PICKER_SENTINEL) {
      chosenCrown = founderCrown;
    } else if (chosenAvatarId === QA_PICKER_SENTINEL) {
      chosenCrown = qaCrown;
    } else if (chosenAvatarId) {
      chosenCrown = crowns.find((c) => c.avatarId === chosenAvatarId) || null;
    }
    if (!chosenCrown && chosenAvatarId !== '__none__') {
      // Auto-pick: the player has a crown but hasn't explicitly
      // picked one yet → show their highest-wins crown. Founders
      // with no crowns get the founder title by default so the
      // thank-you is visible without opening Edit Profile.
      chosenCrown = [...crowns].sort((a, b) => (b.wins || 0) - (a.wins || 0))[0] || null;
      if (!chosenCrown) {
        // Crownless default, QA before founder. Same precedence
        // PlayerTitle applies, so the profile headline and the byline
        // under the name elsewhere never disagree.
        chosenCrown = qaCrown || founderCrown;
      }
    }
    // Edit Profile title picker still uses selectedCrownIndex to flash
    // a hover preview of the chosen title under the name, scoped to
    // that picker UI only. The header strip never sets it.
    const previewCrown = (selectedCrownIndex >= 0 ? crowns[selectedCrownIndex] : null) || null;
    const displayedCrown = previewCrown || chosenCrown;

    // Unified badge list for the strip + Badges modal, and the pinned
    // subset shown in the always-visible header strip. Both are shared
    // with the leaderboard rows so the badges a player pins here are
    // exactly the ones that follow their name on the ladders.
    const allBadges = buildPlayerBadges({
      crowns,
      isFounder: profile.isFounder,
      isQaTester: profile.isQaTester,
    });
    const pinnedBadges = resolvePinnedBadges(allBadges, profile.pinnedBadgeIds);
    const pinnedIds = new Set(pinnedBadges.map((b) => b.id));

    // Toggle a badge's pinned state. Adds to the saved list when
    // there's room, swaps in for the oldest pin when at capacity,
    // removes when already pinned. Persistence happens via the
    // onSavePinnedBadges prop wired in app.jsx.
    const togglePinBadge = async (badgeId) => {
      if (!isSelf || !this.props.onSavePinnedBadges) return;
      const current = pinnedBadges.map((b) => b.id);
      const next = current.includes(badgeId)
        ? current.filter((id) => id !== badgeId)
        : (current.length >= PIN_LIMIT
            ? [...current.slice(1), badgeId]
            : [...current, badgeId]);
      try {
        await this.props.onSavePinnedBadges(next);
        this.setState((s) => ({
          profile: s.profile ? { ...s.profile, pinnedBadgeIds: next } : s.profile,
        }));
      } catch (err) {
        console.warn('[FriendProfileOverlay] Failed to save pinned badges:', err);
      }
    };
    const avatarUrl = resolveAvatarUrl(profile, sorceryCards);

    const progress = xpProgressInLevel(profile.xp || 0);
    const level = progress.level;
    const wins = profile.wins || 0;
    const losses = profile.losses || 0;
    const total = wins + losses;
    const winRate = total > 0 ? Math.round((wins / total) * 100) : 0;
    const isOnline = profile.online;
    const statusText = profile.customStatus || (isOnline ? t('Online') : null);

    // Server-returned decks the player has published to the shared
    // library. Private decks never appear here. Defaults to [] so
    // older server builds without this field render the empty state
    // instead of crashing.
    const publishedDecks = Array.isArray(profile.publishedDecks) ? profile.publishedDecks : [];
    const trophies = Array.isArray(profile.trophies) ? profile.trophies : [];
    const allRecentMatches = replayableMatches(profile.matchHistory);
    const recentQuery = (this.state.recentQuery || '').trim().toLowerCase();
    const recentMatches = recentQuery
      ? allRecentMatches.filter((m) => (m.customName || '').toLowerCase().includes(recentQuery)
        || (m.opponentName || '').toLowerCase().includes(recentQuery))
      : allRecentMatches;
    const achievements = profile.achievements || [];

    // Showcase cards. Pinned trophy cards the player chose to
    // display. Server returns an array of { cardId, foiling }; we
    // cross-check ownership against the player's live collection and
    // resolve each to the catalog entry for art + name. A card that's
    // no longer owned silently drops out (so the showcase never
    // displays trophies the player has since sold).
    const cardCatalog = Array.isArray(sorceryCards) ? sorceryCards : [];
    const cardById = new Map(cardCatalog.map((c) => [c.unique_id, c]));
    const ownedCardSet = new Set((profile.collection || []).map((c) => c.cardId));
    // Resolve each showcase entry into a DeckCardTile-shaped object so
    // the showcase grid can re-use the same tile component the deck
    // builder, auction house, and draft results use. Gives us foil
    // animations, parallax hover, and consistent visual treatment for
    // free. We match the printing by foiling so the right finish (S/F
    // /Rainbow) actually renders, falling back to the first printing
    // if the matching finish isn't available in the catalog.
    const visibleShowcase = (Array.isArray(profile.showcaseCards) ? profile.showcaseCards : [])
      .filter((s) => s?.cardId && ownedCardSet.has(s.cardId))
      .slice(0, SHOWCASE_CAP)
      .map((s, index) => {
        const card = cardById.get(s.cardId);
        const foiling = s.foiling || 'S';
        const printing = resolvePrinting(card, null, foiling);
        return {
          cardId: s.cardId,
          foiling,
          name: card?.name || s.cardId,
          card,
          printing,
          // Shape required by DeckCardTile. Zone is cosmetic only here.
          entry: card && printing
            ? { card, printing, zone: 'spellbook', entryIndex: index }
            : null,
        };
      });

    const setNameMap = { Gothic: 'gothic', 'Arthurian Legends': 'arthurian', Beta: 'beta' };
    const setStats = { gothic: { owned: 0, total: 0, label: 'Gothic' }, arthurian: { owned: 0, total: 0, label: 'Arthurian' }, beta: { owned: 0, total: 0, label: 'Beta' } };
    const ownedCardIds = ownedCardSet;
    if (sorceryCards) {
      for (const card of sorceryCards) {
        const cardSets = new Set((card.printings || []).map(p => p.set_id));
        for (const setName of cardSets) {
          const key = setNameMap[setName];
          if (key) {
            setStats[key].total++;
            if (ownedCardIds.has(card.unique_id)) setStats[key].owned++;
          }
        }
      }
    }

    const showcaseTabs = [
      { key: 'showcase', label: t('Showcase'), count: visibleShowcase.length },
      { key: 'decks', label: t('Published Decks'), count: publishedDecks.length },
      { key: 'trophies', label: t('Trophies'), count: trophies.length },
    ];

    return (
      <div className="flex flex-col h-full min-h-0">
        {/* Top bar. The badge strip is absolutely-centered across the
            full page width so it lives independently of the action
            cluster on the right. The player's earned badges get the
            visual prominence of a banner along the top of the page,
            and they never "share a row" with chat/remove icons.
            The strip is one click target that opens the read-only
            Badges viewer; per-badge clicks no longer set the displayed
            title, since that's an Edit Profile action.
            z-30 raises the WHOLE top bar above the Hero section
            (z-10) that follows in the DOM. The badge tray is
            absolutely-positioned and ~7cqw tall, so its bottom half
            hangs below the top bar's min-height and overlaps the
            Hero's transparent padding band. Without bumping the top
            bar's stacking context, the Hero section captured mouse
            events in that overlap region. Z-index on the tray alone
            doesn't escape its parent stacking context, so the parent
            (this row) is what has to be raised. */}
        <div className="relative shrink-0 flex items-center justify-end px-8 pt-8 z-30">
          {allBadges.length > 0 ? (
            // Positioning wrapper keeps the translateX-centering off
            // the button itself so the button's `hover:scale` doesn't
            // collide with the centering transform.
            //
            // z-30 raises the badge tray above the Hero section that
            // follows in the DOM (also z-10). The tray is ~7cqw tall
            // and absolutely-positioned, so its bottom half hangs
            // below the top bar and overlaps with the Hero's
            // transparent padding. Without this, the Hero captured
            // mouse events in that overlap region and hover only
            // fired on the upper half of the tray.
            <div
              className="absolute left-1/2 top-8 z-30"
              style={{ transform: 'translateX(-50%)' }}
            >
              {/* Framed badge tray. The BUTTON itself is the visible
                  plaque. All visual styling (background / border / glow
                  / hover handlers) lives directly on the button so the
                  click + hover hit area is exactly the rim the player
                  sees. The previous structure wrapped a styled inner
                  div in a transparent button, which left the bottom
                  border edge slightly outside the button's hit box.
                  Fixed-width sized for PIN_LIMIT slots so the layout
                  stays stable. */}
              <button
                type="button"
                className="flex flex-col items-center px-4 pt-2.5 pb-3 transition-all hover:scale-[1.04]"
                style={{
                  width: '17cqw',
                  background: `url("/tex-noise.webp"), linear-gradient(180deg, rgba(14,11,8,0.78) 0%, rgba(8,6,4,0.85) 100%)`,
                  backdropFilter: 'blur(16px)',
                  WebkitBackdropFilter: 'blur(16px)',
                  border: `1px solid ${ACCENT} 0.28)`,
                  borderRadius: 'var(--radius-lg)',
                  boxShadow: `inset 0 1px 0 ${ACCENT_HI}0.08), 0 4px 14px rgba(0,0,0,0.55)`,
                }}
                title={t('View badges ({n})', { n: allBadges.length })}
                aria-label={t('View badges ({n})', { n: allBadges.length })}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = `${ACCENT} 0.6)`; e.currentTarget.style.boxShadow = `inset 0 1px 0 ${ACCENT_HI}0.14), 0 6px 18px rgba(0,0,0,0.6), 0 0 14px ${ACCENT} 0.25)`; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = `${ACCENT} 0.28)`; e.currentTarget.style.boxShadow = `inset 0 1px 0 ${ACCENT_HI}0.08), 0 4px 14px rgba(0,0,0,0.55)`; }}
                onClick={() => this.setState({ showBadgesModal: true })}
              >
                <div
                  className="arena-heading font-semibold leading-none"
                  style={{
                    color: ACCENT_TEXT,
                    fontSize: '0.8125cqw',
                    textShadow: `0 1px 2px rgba(0,0,0,0.85), 0 0 8px ${ACCENT_BRIGHT} 0.35)`,
                  }}
                >
                  {t('Badges')}
                </div>
                <div
                  className="w-full mt-1.5 mb-2 h-px"
                  style={{
                    background: `linear-gradient(90deg, transparent 0%, ${ACCENT} 0.5) 50%, transparent 100%)`,
                  }}
                />
                <div className="flex items-center justify-center gap-3 w-full">
                  {Array.from({ length: PIN_LIMIT }).map((_, slotIdx) => {
                    const badge = pinnedBadges[slotIdx];
                    if (!badge) {
                      return (
                        <span
                          key={`slot-${slotIdx}`}
                          aria-hidden="true"
                          style={{
                            width: '3.4cqw',
                            height: '4.25cqw',
                            display: 'inline-flex',
                            alignItems: 'flex-end',
                          }}
                        >
                          <span
                            style={{
                              width: '3.4cqw',
                              height: '3.4cqw',
                              borderRadius: '50%',
                              border: `1px dashed ${ACCENT} 0.18)`,
                              background: 'rgba(0,0,0,0.25)',
                              display: 'block',
                            }}
                          />
                        </span>
                      );
                    }
                    const isActive = chosenCrown?.avatarId === badge.id;
                    return (
                      <span
                        key={badge.id}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'flex-end',
                          height: '4.25cqw',
                          transform: isActive ? 'translateY(-1px)' : 'translateY(0)',
                          transition: 'transform 0.15s ease',
                        }}
                      >
                        <AvatarCrownBadge
                          crown={badge}
                          size={isActive ? '3.85cqw' : '3.4cqw'}
                          active={isActive}
                          showTooltip={false}
                        />
                      </span>
                    );
                  })}
                </div>
              </button>
            </div>
          ) : null}
          {/* Action cluster. Small icon buttons sitting alongside
              Close. Chat is the primary affordance (gold tint); Remove
              Friend is a quiet ghost button that shifts red on hover.
              Both are icon-only so they don't compete with the player
              name and crowns below. The Back affordance was removed.
              the X is the only dismiss target, matching the rest of
              the app's modal-style pages. */}
          <div className="flex items-center gap-2">
            {isSelf ? (
              <button
                type="button"
                title={t('Edit Profile')}
                aria-label={t('Edit Profile')}
                onClick={() => this.setState({ editProfileOpen: true })}
                className="cursor-pointer transition-all flex items-center justify-center"
                style={{
                  width: '2.25cqw',
                  height: '2.25cqw',
                  borderRadius: 'var(--radius-md)',
                  background: 'rgb(20, 14, 6)',
                  border: `1px solid ${ACCENT} 0.32)`,
                  color: ACCENT_TEXT,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = `color-mix(in srgb, ${ACCENT_BRIGHT} 1) 18%, rgb(20, 14, 6))`;
                  e.currentTarget.style.borderColor = `${ACCENT} 0.55)`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgb(20, 14, 6)';
                  e.currentTarget.style.borderColor = `${ACCENT} 0.32)`;
                }}
              >
                <svg style={{ width: '1cqw', height: '1cqw' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
              </button>
            ) : null}
            {this.props.onViewSkillTree ? (
              <button
                type="button"
                title={t('View Skill Tree')}
                aria-label={t('View Skill Tree')}
                onClick={() => this.props.onViewSkillTree(profile)}
                className="cursor-pointer transition-all flex items-center justify-center"
                style={{
                  width: '2.25cqw',
                  height: '2.25cqw',
                  borderRadius: 'var(--radius-md)',
                  background: 'rgb(20, 14, 6)',
                  border: `1px solid ${ACCENT} 0.32)`,
                  color: ACCENT_TEXT,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = `color-mix(in srgb, ${ACCENT_BRIGHT} 1) 18%, rgb(20, 14, 6))`;
                  e.currentTarget.style.borderColor = `${ACCENT} 0.55)`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgb(20, 14, 6)';
                  e.currentTarget.style.borderColor = `${ACCENT} 0.32)`;
                }}
              >
                {/* Shared skill-tree glyph. Same icon the AppHeader
                    uses for the main-menu skill tree button, so both
                    surfaces read as the same affordance. */}
                <IconSkillTree style={{ width: '1cqw', height: '1cqw' }} />
              </button>
            ) : null}
            {isFriend !== false && onRemoveFriend ? (
              <button
                type="button"
                title={t('Remove Friend')}
                aria-label={t('Remove Friend')}
                onClick={() => this.setState({ showUnfriendConfirm: true })}
                className="cursor-pointer transition-all flex items-center justify-center"
                style={{
                  width: '2.25cqw',
                  height: '2.25cqw',
                  borderRadius: 'var(--radius-md)',
                  background: 'rgb(20, 14, 6)',
                  border: `1px solid ${ACCENT} 0.28)`,
                  color: TEXT_MUTED,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = '#e07060';
                  e.currentTarget.style.borderColor = 'rgba(224, 112, 96, 0.55)';
                  e.currentTarget.style.background = 'color-mix(in srgb, rgb(60, 18, 14) 70%, rgb(20, 14, 6))';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = TEXT_MUTED;
                  e.currentTarget.style.borderColor = `${ACCENT} 0.28)`;
                  e.currentTarget.style.background = 'rgb(20, 14, 6)';
                }}
              >
                <svg style={{ width: '1cqw', height: '1cqw' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M17 11l5 5M22 11l-5 5" />
                </svg>
              </button>
            ) : null}
            <CloseButton onClick={this.requestClose} style={{ backgroundColor: 'rgb(20, 14, 6)' }} />
          </div>
        </div>

        <ProfileHero
          profile={profile}
          avatarUrl={avatarUrl}
          displayedCrown={displayedCrown}
          level={level}
          isOnline={isOnline}
          statusText={statusText}
          wins={wins}
          losses={losses}
          winRate={winRate}
          onChangeAvatar={isSelf ? () => this.setState({ editProfileOpen: true, editProfileTab: 'avatar' }) : null}
          onChallenge={onChallenge && isOnline && !isSelf ? (mode) => onChallenge({ id: profileId, name: profile.name }, mode) : null}
          onMessage={onSendMail && !isSelf ? () => onSendMail(profileId) : null}
        />


        {/* Body. Full-width 3-column grid spanning the canvas with
            only a tight gutter. No centered "page rail." The layout
            reads as a real game profile screen, not a webpage. The
            right rail (Decks / Showcase) owns half the width because
            it's the most visual element; the activity feed in the
            middle and the progression rail on the left both stay
            compact. Recent Matches and Achievements render INLINE as
            scrollable lists rather than tile + modal. There's enough
            real estate here that hiding them behind a "View All" was
            never necessary. */}
        <div className="flex-1 min-h-0 px-8 pb-6 overflow-hidden">
          <div className="h-full grid grid-cols-12 gap-3">
          {/* ── LEFT COLUMN. Treasury and recent
              matches. The Battle Record panel lives in the hero now,
              sitting next to the avatar so the column can give the
              remaining widgets. Most importantly the recent matches
              list. More vertical room. */}
          <div className="col-span-3 flex flex-col gap-3 min-h-0 h-full">
            {/* Treasury. Gold, arcana shards, keystones. Server
                already returns these on the public profile endpoint,
                so any visitor can see how rich the player is. Three
                equal columns with icon + tabular number; the icons
                carry their own colour signature so the numbers don't
                need a label. */}
            {/* Treasury is the owner's business; visitors get the record
                in the hero and the rest of the column. */}
            {isSelf ? (
            <div className="vh-profile-rise px-4 py-3" style={{ ...STONE_RECESS_STYLE, '--i': 5 }}>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { key: 'coins', value: profile.coins || 0, label: t('Gold'), color: '#f0d060', Icon: CoinIcon },
                  { key: 'arcana', value: profile.arcanaShards || 0, label: t('Arcana'), color: '#7dd3fc', Icon: ShardIcon },
                  { key: 'keystones', value: profile.keystones || 0, label: t('Keystones'), color: '#c9a86a', Icon: KeystoneIcon },
                ].map(({ key, value, label, color, Icon }) => (
                  <div
                    key={key}
                    className="flex flex-col items-center min-w-0"
                    title={t('{value} {label}', { value: value.toLocaleString(), label })}
                  >
                    <div className="flex items-center gap-1.5 min-w-0">
                      <Icon size={16} />
                      <span
                        className="font-bold tabular-nums truncate"
                        style={{ color, fontSize: '0.875cqw', lineHeight: 1, textShadow: '0 1px 2px rgba(0,0,0,0.65)' }}
                      >
                        {value.toLocaleString()}
                      </span>
                    </div>
                    <span className="mt-0.5" style={{ color: TEXT_MUTED, fontSize: '0.5cqw' }}>{label}</span>
                  </div>
                ))}
              </div>
            </div>
            ) : null}

            {/* Recent Matches. Moved into the left rail as the
                column's tall flex-grow panel. Header carries a subtle
                W/L dot strip (one dot per recorded match, gold rim
                for wins, red for losses) so the player gets an at-a-
                glance read on the streak before scanning the list. */}
            <div className="vh-profile-rise flex flex-col p-4" style={{ ...STONE_RECESS_STYLE, flex: '1 1 0', minHeight: 0, '--i': 7 }}>
              {/* Heading over the W/L strip in a left column, search box
                  beside them. The column is no taller than the input, so
                  the list keeps the height the banner rules and a second
                  row used to take. */}
              <div className="flex items-center gap-3 w-full mb-2.5">
                <div className="flex flex-col gap-1 shrink-0">
                  <span
                    className="text-[0.5625cqw] font-semibold leading-none"
                    style={{ color: ACCENT_TEXT, textShadow: `0 0 8px ${ACCENT} 0.35)` }}
                  >
                    {t('Recent Matches')}
                    {recentMatches.length > 0 ? (
                      <span className="ml-1.5 tabular-nums" style={{ color: TEXT_MUTED }}>· {recentMatches.length}</span>
                    ) : null}
                  </span>
                  {recentMatches.length > 0 ? (
                    <div className="flex items-center gap-1">
                      {/* History runs 100 deep since replays landed; the
                          strip stays at the ten newest. */}
                      {recentMatches.slice(0, 10).map((m, i) => (
                        <span
                          key={i}
                          className="inline-block rounded-full"
                          style={{
                            width: '0.4cqw',
                            height: '0.4cqw',
                            background: m.won ? '#7ad06a' : '#e07060',
                            boxShadow: m.won ? '0 0 4px rgba(122,208,106,0.55)' : '0 0 4px rgba(224,112,96,0.5)',
                            opacity: 0.9,
                          }}
                          title={m.won ? t('Win vs {name}', { name: m.opponentName || t('Opponent') }) : t('Loss vs {name}', { name: m.opponentName || t('Opponent') })}
                        />
                      ))}
                    </div>
                  ) : null}
                </div>
                {allRecentMatches.length > 0 ? (
                  <input
                    type="text"
                    value={this.state.recentQuery || ''}
                    placeholder={t('Search matches...')}
                    className="ml-auto shrink-0 px-2 py-1 rounded-md text-[0.625cqw] outline-none"
                    // The panel corner (radius-lg) is smaller than the 1cqw
                    // inset, so a strictly concentric radius is 0; the
                    // control step of the ramp reads right instead.
                    style={{ width: '8cqw', background: 'rgba(0,0,0,0.3)', border: `1px solid ${ACCENT} 0.15)`, color: TEXT_PRIMARY }}
                    onInput={(e) => this.setState({ recentQuery: e.currentTarget.value })}
                  />
                ) : null}
              </div>
              {/* The scroll box reaches into the panel's padding and pads
                  itself back, so a hovered card's lift and glow have room
                  instead of clipping at the box edge. */}
              <div className="flex-1 min-h-0 overflow-y-auto -mx-3 -mb-3 px-3 pb-3 pt-2" style={{ scrollbarWidth: 'none' }}>
                {recentMatches.length > 0 ? (
                  <div className="grid grid-cols-1 gap-2">
                    {/* The same card the Past Matches tab draws. One column:
                        at half the rail's width the portraits collapse and
                        both names truncate. Someone else's match opens only
                        if they shared it with the viewer by DM or posted it
                        in the Tavern (the server marks both on the row);
                        the rest stay disabled. */}
                    {recentMatches.map((m, i) => (
                      <ReplayHistoryCard
                        key={`${m.roomCode || 'm'}-${i}`}
                        match={m}
                        profile={profile}
                        sorceryCards={sorceryCards}
                        canWatch={!!this.props.onWatchReplay && (isSelf || !!m.replayShared || !!m.replayLink)}
                        onWatch={this.props.onWatchReplay}
                        onUpdateMatchMeta={isSelf && this.props.onUpdateMatchMeta ? this.handleUpdateMatchMeta : null}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="h-full flex items-center justify-center text-[0.6875cqw] italic" style={{ color: TEXT_MUTED }}>
                    {t('No matches yet')}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── RIGHT COLUMN. Collection + Achievements. Source-defined after the
              left column for HTML reading/tab order, but pushed to
              the visual right via grid `order` so the deck/showcase
              tabs sit in the middle (where the eye lands naturally
              after scanning the hero). */}
          <div className="col-span-3 flex flex-col gap-3 min-h-0 h-full" style={{ order: 2 }}>
            {/* Collection. Natural height (three bars + total) above
                the tall Achievements panel. */}
            <div className="vh-profile-rise px-4 py-3" style={{ ...STONE_RECESS_STYLE, '--i': 6 }}>
              <div
                className="text-[0.625cqw] text-center mb-2"
                style={{ color: TEXT_MUTED }}
              >
                {t('{unique} unique · {total} total', { unique: profile.uniqueCards || 0, total: profile.collectionSize || 0 })}
              </div>
              <div className="flex flex-col gap-1.5">
                {['gothic', 'arthurian', 'beta'].map((key) => {
                  const s = setStats[key];
                  const pct = s.total > 0 ? Math.round((s.owned / s.total) * 100) : 0;
                  return (
                    <div key={key} className="flex items-center gap-2">
                      <span className="text-[0.625cqw] w-16 truncate" style={{ color: TEXT_BODY }}>{s.label}</span>
                      <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: 'rgba(0,0,0,0.55)', boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.7)' }}>
                        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: SET_COLORS[key] || `${ACCENT} 0.4)`, boxShadow: `0 0 6px ${ACCENT_BRIGHT}0.4)` }} />
                      </div>
                      <span className="text-[0.625cqw] tabular-nums w-14 text-right" style={{ color: TEXT_MUTED }}>{s.owned}/{s.total}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="vh-profile-rise flex flex-col flex-1 min-h-0 px-4 py-3" style={{ ...STONE_RECESS_STYLE, '--i': 6 }}>
              <SectionBanner count={`${achievements.length}/${ACHIEVEMENTS.length}`}>{t('Achievements')}</SectionBanner>
              {/* Completion gauge. Pearl like the medallions it counts,
                  and like them fixed rather than following the accent. */}
              <div className="flex items-center gap-2 mb-2">
                <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(0,0,0,0.55)', boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.7)' }}>
                  <div
                    className="h-full rounded-full transition-all"
                    style={{
                      width: `${ACHIEVEMENTS.length > 0 ? Math.round((achievements.length / ACHIEVEMENTS.length) * 100) : 0}%`,
                      background: `linear-gradient(90deg, ${PEARL.edge} 0%, ${PEARL.body} 60%, ${PEARL.hi} 100%)`,
                      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.7), 0 0 6px rgba(255, 248, 230, 0.35)',
                    }}
                  />
                </div>
                <span className="text-[0.5625cqw] tabular-nums shrink-0 font-bold" style={{ color: PEARL.body, textShadow: '0 1px 2px rgba(0,0,0,0.6)' }}>
                  {ACHIEVEMENTS.length > 0 ? Math.round((achievements.length / ACHIEVEMENTS.length) * 100) : 0}%
                </span>
              </div>
              {/* Same panel as the hub. Claim only wires for self; the
                  public profile omits unclaimedAchievements, so the
                  self-view reads the hub profile's list via prop. */}
              <div className="flex-1 min-h-0 flex flex-col -mx-1">
                <AchievementsPanel
                  header={false}
                  profile={profile}
                  sorceryCards={sorceryCards}
                  customDeckCount={isSelf ? this.props.customDeckCount : 0}
                  maxDeckLikes={isSelf ? this.props.maxDeckLikes : 0}
                  onClaimAchievement={isSelf ? this.props.onClaimAchievement : null}
                  unclaimedAchievements={isSelf ? this.props.unclaimedAchievements : null}
                  canvasScale={this.state.canvasScale}
                />
              </div>
            </div>
          </div>

          {/* ── CENTER COLUMN. Tabbed Decks / Showcase rail. Owns
              half the canvas because it's the most visually
              substantial part of the profile (deck cards on one tab,
              card art on the other). `order: 1` places it between
              the left column (default order 0) and the achievements
              column (order 2) regardless of source position. */}
          <div className="col-span-6 flex flex-col min-h-0 h-full" style={{ order: 1 }}>
            {/* overflow-hidden so the tab-bar background gradients
                and underline rule clip cleanly to the panel's rounded
                corners instead of squaring off in the top corners. */}
            <div className="relative vh-profile-rise flex flex-col flex-1 min-h-0 overflow-hidden" style={{ ...STONE_SECTION_STYLE, '--i': 5 }}>
              <FourCorners color={`${ACCENT} 0.55)`} inset={0} />
              {/* Tab bar. Same rail the settings screen uses (vh-top-tab):
                  dim label by default, warms on hover with a short
                  indicator bar, full gold with the bar sliding wide when
                  active. Compact variant because this rail lives inside a
                  panel rather than across the whole screen. */}
              <TabBar
                className="shrink-0 flex items-stretch"
                variant="chapter"
                activeIndex={showcaseTabs.findIndex((tab) => tab.key === activeShowcaseTab)}
              >
                {showcaseTabs.map((tab) => {
                  const isActive = activeShowcaseTab === tab.key;
                  return (
                    <button
                      key={tab.key}
                      type="button"
                      onClick={() => this.setState({
                        activeShowcaseTab: tab.key,
                        // Leaving the Showcase tab implicitly exits edit mode
                        // so the toggle never lingers invisibly on the other tab.
                        showcaseEditMode: tab.key === 'showcase' ? this.state.showcaseEditMode : false,
                      })}
                      data-tab-item
                      className={`vh-top-tab vh-top-tab-compact flex-1 justify-center relative ${isActive ? 'vh-top-tab-active' : ''}`}
                    >
                      {tab.label}
                      {tab.count > 0 ? (
                        <span className="text-[0.625cqw] tabular-nums opacity-70">· {tab.count}</span>
                      ) : null}
                      {/* Showcase edit toggle. Self only, and only while the
                          Showcase tab is active, so it sits with the thing it
                          edits instead of at the far end of the rail. Pinned to
                          the tab's right edge and taken out of flow, so the
                          label stays centred whether or not it is showing. A
                          span rather than a nested button; the click is stopped
                          before it re-triggers the tab. Armed state fills gold
                          so the player can tell at a glance whether clicking a
                          card will inspect it or swap it. */}
                      {isSelf && tab.key === 'showcase' && isActive ? (
                        <span
                          role="button"
                          tabIndex={0}
                          title={showcaseEditMode ? t('Exit edit mode') : t('Edit showcase')}
                          aria-label={showcaseEditMode ? t('Exit edit mode') : t('Edit showcase')}
                          aria-pressed={showcaseEditMode}
                          onClick={(e) => {
                            e.stopPropagation();
                            this.setState((s) => ({ showcaseEditMode: !s.showcaseEditMode }));
                          }}
                          onKeyDown={(e) => {
                            if (e.key !== 'Enter' && e.key !== ' ') return;
                            e.preventDefault();
                            e.stopPropagation();
                            this.setState((s) => ({ showcaseEditMode: !s.showcaseEditMode }));
                          }}
                          className="absolute flex items-center justify-center cursor-pointer transition-colors"
                          style={{
                            right: '0.5cqw',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            width: '1.375cqw',
                            height: '1.375cqw',
                            borderRadius: 'var(--radius-sm)',
                            background: showcaseEditMode ? ACCENT_TEXT : `${ACCENT_BRIGHT} 0.14)`,
                            border: `1px solid ${showcaseEditMode ? ACCENT_TEXT : `${ACCENT} 0.4)`}`,
                            color: showcaseEditMode ? '#0e0a06' : ACCENT_TEXT,
                            boxShadow: showcaseEditMode ? `0 0 12px ${ACCENT} 0.55)` : 'none',
                          }}
                          onMouseEnter={(e) => {
                            if (!showcaseEditMode) e.currentTarget.style.background = `${ACCENT_BRIGHT} 0.26)`;
                          }}
                          onMouseLeave={(e) => {
                            if (!showcaseEditMode) e.currentTarget.style.background = `${ACCENT_BRIGHT} 0.14)`;
                          }}
                        >
                          <svg style={{ width: '0.75cqw', height: '0.75cqw' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                          </svg>
                        </span>
                      ) : null}
                    </button>
                  );
                })}
              </TabBar>
              {/* Gold hairline the active tab's indicator bar sits on,
                  fading out before the panel's rounded corners. */}
              <div
                className="shrink-0 h-px"
                style={{ background: `linear-gradient(90deg, transparent 0%, ${ACCENT} 0.35) 12%, ${ACCENT} 0.35) 88%, transparent 100%)` }}
              />

              {/* Tab body. Keyed on the tab so a switch remounts it
                  through the slide-in. */}
              <div key={activeShowcaseTab} className="vh-profile-tab-in flex-1 min-h-0 px-4 py-4 flex flex-col">
                {activeShowcaseTab === 'decks' ? (
                  publishedDecks.length > 0 ? (
                    /* Library-style tile grid. Uses the same horizontal
                       DeckTile component as the deck collection and
                       library views, so a player's published decks read
                       as part of the same forged-plaque family. Clicking
                       opens the deck in the editor via the same
                       sandbox-preview flow as library entries. */
                    <div
                      className="flex-1 min-h-0 overflow-y-auto grid gap-3 auto-rows-min"
                      style={{
                        gridTemplateColumns: 'repeat(auto-fill, minmax(20cqw, 1fr))',
                        // Padding gives each tile room to grow on hover
                        // (the wrapper scale + fan spread can extend the
                        // visible bounds slightly past the tile box). Without
                        // it, tiles in the edge columns clip when scaled.
                        padding: '0.75cqw 1cqw',
                        margin: '-0.4cqw -0.4cqw 0',
                      }}
                    >
                      {publishedDecks.map((deck) => {
                        const cardCount = deck.cardCount ?? (Array.isArray(deck.cards) ? deck.cards.length : 0);
                        const locked = !!this.props.lockNavigation;
                        const footerLeft = (
                          <>
                            {DeckTileText.title(deck.name)}
                            <div className="flex items-center gap-1.5 mt-0.5">
                              {deck.updatedAt ? (
                                <span style={{ color: TEXT_MUTED, fontSize: '0.58cqw' }}>{timeAgo(deck.updatedAt)}</span>
                              ) : null}
                              <span style={{ color: TEXT_MUTED, fontSize: '0.58cqw' }}>· {t('{n} cards', { n: cardCount })}</span>
                            </div>
                          </>
                        );
                        const footerRight = (
                          <div className="flex items-center gap-2">
                            <span className="flex items-center gap-0.5" style={{ color: TEXT_MUTED, fontSize: '0.58cqw' }} title={t('Likes')}>
                              <svg style={{ width: '0.58cqw', height: '0.58cqw' }} viewBox="0 0 24 24" fill="currentColor" stroke="none">
                                <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                              </svg>
                              {deck.likes || 0}
                            </span>
                            <span className="flex items-center gap-0.5" style={{ color: TEXT_MUTED, fontSize: '0.58cqw' }} title={t('Views')}>
                              <svg style={{ width: '0.58cqw', height: '0.58cqw' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                <circle cx="12" cy="12" r="3" />
                              </svg>
                              {deck.views || 0}
                            </span>
                          </div>
                        );
                        return (
                          <div
                            key={deck.id}
                            className="vh-profile-lift"
                            style={{ opacity: locked ? 0.55 : 1, pointerEvents: locked ? 'none' : 'auto' }}
                            title={locked ? t('Finish the match to open this deck') : undefined}
                          >
                            <DeckTile
                              deck={deck}
                              sorceryCards={sorceryCards}
                              onClick={() => { if (!locked) this.props.onOpenLibraryDeck?.(deck); }}
                              footerLeftSlot={footerLeft}
                              footerRightSlot={footerRight}
                            />
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="flex-1 flex items-center justify-center text-[0.6875cqw] italic text-center" style={{ color: TEXT_MUTED }}>
                      {t('No published decks yet')}
                    </div>
                  )
                ) : activeShowcaseTab === 'trophies' ? (
                  <ProfileTrophies trophies={trophies} />
                ) : (
                  /* Showcase tab body. For self, each slot up to 6 is
                     rendered (filled = card image, empty = clickable
                     "+ Add card" tile). For others, only the filled
                     slots render. No awkward empty slots advertising
                     that the player hasn't picked anything. */
                  /* Showcase grid. Six slots in a fixed 3×2 layout that
                     always fits without scrolling. Filled slots render
                     via DeckCardTile so they pick up the same foil
                     animations + parallax hover the deck builder and
                     auction house use. For self, empty slots render
                     as dashed gold tiles that open the picker. */
                  (() => {
                    const hasAnyCards = visibleShowcase.length > 0;
                    if (!isSelf && !hasAnyCards) {
                      return (
                        <div className="flex-1 flex flex-col items-center justify-center gap-2 text-center" style={{ color: TEXT_MUTED }}>
                          <svg style={{ width: '2cqw', height: '2cqw', opacity: 0.4 }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="6" y="3" width="12" height="18" rx="1.5" />
                            <path d="M9 8h6M9 12h6M9 16h3" />
                          </svg>
                          <div className="text-[0.75cqw] italic">{t('No showcase cards yet')}</div>
                        </div>
                      );
                    }
                    // Showcase tiles now route their foil art through
                    // CardGrid's one shared WebGL scene (no per-tile
                    // FoilCanvas churn on hover). Self-view renders all
                    // SHOWCASE_CAP slots (filled cards + "+ Add" buttons);
                    // others render only filled slots. CardGrid owns its own
                    // 4-column scroll container. With at most two rows it
                    // never actually scrolls, so the panel still fits.
                    const slotItems = (isSelf
                      ? Array.from({ length: SHOWCASE_CAP }, (_, i) => ({ slot: visibleShowcase[i] || null, index: i }))
                      : visibleShowcase
                          .map((slot, i) => ({ slot, index: i }))
                          .filter((it) => it.slot?.entry));
                    const showcaseCols = Math.max(1, Math.min(4, slotItems.length));
                    const showcaseRows = slotItems.length > 4 ? 2 : 1;

                    const getShowcaseFoil = (it) => {
                      const printing = it.slot?.entry?.printing;
                      if (!printing || !isFoilFinish(printing.foiling)) return null;
                      return { imageUrl: printing.image_url || '', zones: foilZonesForCard(it.slot.entry.card) };
                    };

                    const renderShowcaseTile = (it, { imageHidden, onTilt }) => {
                      const { slot, index } = it;
                      if (slot?.entry) {
                        return (
                          <div className="relative">
                            <ShowcasePlinth />
                            <DeckCardTile
                              entry={slot.entry}
                              isSelected={false}
                              imageHidden={imageHidden}
                              onTilt={onTilt}
                              // In edit mode (self only) clicking a filled card
                              // opens the picker so the player can swap that
                              // slot. Otherwise clicking opens the inspector.
                              onClick={() => {
                                if (isSelf && showcaseEditMode) this.openReplaceSlot(index);
                                else this.setState({ inspectedShowcaseEntry: slot.entry });
                              }}
                              onHoverChange={(hovering) => this.setState({
                                hoveredShowcaseEntry: hovering ? slot.entry : null,
                              })}
                            />
                            {/* Edit-mode overlay scrim. Visually signals that
                                clicking the card swaps it instead of inspecting
                                it. Sits below the DeckCardTile's hover treatment
                                via pointer-events:none. */}
                            {isSelf && showcaseEditMode ? (
                              <div
                                aria-hidden="true"
                                className="absolute inset-0 pointer-events-none"
                                style={{
                                  borderRadius: RADIUS.card,
                                  border: `1.5px solid ${ACCENT_TEXT}`,
                                  boxShadow: `0 0 12px ${ACCENT} 0.45)`,
                                  background: `${ACCENT_BRIGHT} 0.08)`,
                                }}
                              />
                            ) : null}
                          </div>
                        );
                      }
                      // An empty niche. Hidden until the pointer (or keyboard
                      // focus) reaches it, so the panel reads as the cards on
                      // display rather than a form waiting to be filled.
                      return (
                        <div className="relative" style={{ aspectRatio: '63 / 88' }}>
                          <button
                            type="button"
                            onClick={() => this.openReplaceSlot(index)}
                            className="absolute inset-0 cursor-pointer transition-all flex items-center justify-center opacity-0 hover:opacity-100 focus-visible:opacity-100"
                            style={{
                              background: 'linear-gradient(180deg, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.42) 100%)',
                              border: `1px solid ${ACCENT} 0.14)`,
                              borderRadius: RADIUS.card,
                              boxShadow: 'inset 0 2px 12px rgba(0,0,0,0.65), inset 0 0 0 1px rgba(255,255,255,0.02)',
                              color: ACCENT_TEXT,
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.borderColor = `${ACCENT} 0.5)`;
                              e.currentTarget.style.boxShadow = `inset 0 2px 12px rgba(0,0,0,0.5), 0 0 14px ${ACCENT} 0.25)`;
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.borderColor = `${ACCENT} 0.14)`;
                              e.currentTarget.style.boxShadow = 'inset 0 2px 12px rgba(0,0,0,0.65), inset 0 0 0 1px rgba(255,255,255,0.02)';
                            }}
                          >
                            <div className="flex flex-col items-center gap-1">
                              <span
                                className="flex items-center justify-center rounded-full"
                                style={{ width: '1.25cqw', height: '1.25cqw', border: `1px solid ${ACCENT} 0.7)` }}
                              >
                                <svg style={{ width: '0.625cqw', height: '0.625cqw' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 6v12M6 12h12" /></svg>
                              </span>
                              <span className="text-[0.5cqw]">{t('Add card')}</span>
                            </div>
                          </button>
                        </div>
                      );
                    };

                    return (
                      <div className="flex-1 min-h-0 flex flex-col relative">
                        {/* Edit-mode hint, in the headroom above the first
                            row so it costs the cards nothing. */}
                        {isSelf ? (
                          <span
                            className="absolute text-[0.625cqw] italic pointer-events-none transition-opacity"
                            style={{ top: '0.05cqw', left: '0.25cqw', color: TEXT_MUTED, opacity: showcaseEditMode ? 1 : 0 }}
                          >
                            {t('Click a card to swap it')}
                          </span>
                        ) : null}
                        <ShowcaseFrame canvasScale={this.state.canvasScale} cols={showcaseCols} rows={showcaseRows}>
                          <CardGrid
                            items={slotItems}
                            gridSize={showcaseCols}
                            vpad={SHOWCASE_VPAD}
                            scrollPadClassName="px-3"
                            getItemKey={(it) => (it.slot ? `${it.slot.cardId}-${it.index}` : `empty-${it.index}`)}
                            getItemFoil={getShowcaseFoil}
                            renderTile={renderShowcaseTile}
                          />
                        </ShowcaseFrame>
                      </div>
                    );
                  })()
                )}
              </div>
            </div>
          </div>
        </div>
        </div>

        {/* Remove-friend confirmation. Slides in from the top of the
            page only when the unfriend icon is tapped. The regular
            footer no longer exists because all friend actions live as
            icon buttons in the top bar. */}
        {showUnfriendConfirm ? (
          <div
            className="shrink-0 px-10 py-4"
            style={{
              borderTop: `1px solid rgba(224, 112, 96, 0.35)`,
              background: 'linear-gradient(180deg, transparent 0%, rgba(40, 12, 10, 0.55) 100%)',
            }}
          >
            <div className="max-w-[64cqw] mx-auto flex items-center gap-3">
              <span className="text-[0.75cqw] flex-1" style={{ color: TEXT_BODY }}>
                {t('Remove {name} from friends?', { name: profile.name })}
              </span>
              <button
                type="button"
                className="px-4 py-2 text-[0.6875cqw] cursor-pointer transition-all"
                style={{ ...BEVELED_BTN, backgroundColor: 'rgb(20, 14, 6)', color: TEXT_BODY, borderRadius: 'var(--radius-md)' }}
                onClick={() => this.setState({ showUnfriendConfirm: false })}
              >
                {t('Cancel')}
              </button>
              <button
                type="button"
                className="px-4 py-2 text-[0.6875cqw] font-medium cursor-pointer transition-all"
                style={{ ...DANGER_BTN, borderRadius: 'var(--radius-md)' }}
                onClick={() => { if (onRemoveFriend) onRemoveFriend(profileId); onClose(); }}
              >
                {t('Remove')}
              </button>
            </div>
          </div>
        ) : null}

        {/* Edit Profile modal. Self only. Three sub-tabs (Portrait /
            Background / Title) each scoped to one piece of the player's
            cosmetic state. Tabs scale better than stacking everything in
            one scroll because the picker grids stay large enough to
            actually browse without losing the search field above. */}
        {editProfileOpen ? (() => {
          const search = (editProfileSearch || '').trim().toLowerCase();
          // Two entitlement sources feed both pickers:
          //   collection      → Avatar cards earned from booster packs
          //                     (only Avatars are surfaced as portraits)
          //   ownedCosmetics  → cards unlocked via cosmetic store packs;
          //                     each unlock works as BOTH portrait AND
          //                     background, regardless of card type.
          const collectionSet = new Set((profile.collection || []).map((c) => c.cardId));
          const cosmeticSet = new Set(profile.ownedCosmetics || []);

          // Portrait tab: Avatar-type cards from collection, plus
          // non-Site cosmetic unlocks (from portrait packs). Site
          // unlocks belong to the Background tab only.
          const ownedAvatars = (Array.isArray(sorceryCards) ? sorceryCards : [])
            .filter((c) => {
              const fromCollection = c.type === 'Avatar' && collectionSet.has(c.unique_id);
              const fromCosmetic = cosmeticSet.has(c.unique_id) && c.type !== 'Site';
              return fromCollection || fromCosmetic;
            })
            .filter((c) => !search || (c.name || '').toLowerCase().includes(search))
            .sort((a, b) => {
              // Avatars (booster-earned) sort first, then cosmetics
              // by name. Within either group, name order.
              const ao = a.type === 'Avatar' ? 0 : 1;
              const bo = b.type === 'Avatar' ? 0 : 1;
              if (ao !== bo) return ao - bo;
              return (a.name || '').localeCompare(b.name || '');
            });
          // Background tab: only Site cards. Every player has the four
          // basic realms (Spire / Stream / Valley / Wasteland) for
          // free from day one. Same pool the Draft UI offers as
          // unlimited basics. Plus any additional Sites the player
          // has unlocked through a Realm Pack. Non-Site cards never
          // appear here, even if they were rolled from a portrait pack.
          const RARITY_ORDER = { 'Unique': 0, 'Elite': 1, 'Exceptional': 2, 'Ordinary': 3 };
          const isBackgroundEntitled = (c) => {
            if (c.type !== 'Site') return false;
            if (!c.printings?.[0]?.image_url) return false;
            return isBasicSite(c.unique_id) || cosmeticSet.has(c.unique_id);
          };
          const ownedBackgrounds = (Array.isArray(sorceryCards) ? sorceryCards : [])
            .filter(isBackgroundEntitled)
            .filter((c) => !search || (c.name || '').toLowerCase().includes(search))
            .sort((a, b) => {
              // Basic realms first (always-available starter set), then
              // unlocked Sites by rarity (Unique → Ordinary), then name.
              const aBasic = isBasicSite(a.unique_id) ? 0 : 1;
              const bBasic = isBasicSite(b.unique_id) ? 0 : 1;
              if (aBasic !== bBasic) return aBasic - bBasic;
              const ao = RARITY_ORDER[a.rarity] ?? 99;
              const bo = RARITY_ORDER[b.rarity] ?? 99;
              if (ao !== bo) return ao - bo;
              return (a.name || '').localeCompare(b.name || '');
            });
          const titleHits = crowns.filter((c) => !search
            || (c.label || '').toLowerCase().includes(search)
            || (c.title || '').toLowerCase().includes(search)
            || (c.avatarName || '').toLowerCase().includes(search));

          // When the player has no explicit pick (null), PlayerTitle
          // auto-displays their highest-wins crown. Mirror that here so
          // the picker accurately reflects what's rendered on the
          // profile. Otherwise a crown-holder who hasn't visited Edit
          // Profile would see "No Title" implied, but their profile
          // would still show a title.
          const explicitCrownId = profile.displayedCrownAvatarId || null;
          const autoCrown = (!explicitCrownId && crowns.length > 0)
            ? [...crowns].sort((a, b) => (b.wins || 0) - (a.wins || 0))[0]
            : null;

          const setTab = (tab) => this.setState({ editProfileTab: tab, editProfileSearch: '' });

          return (
          <div
            className="absolute inset-0 z-40 flex items-center justify-center"
            style={{ background: 'radial-gradient(ellipse 80% 70% at 50% 50%, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.94) 100%)' }}
            onClick={() => this.setState({ editProfileOpen: false })}
          >
            <div
              className="w-full max-w-[60cqw] mx-4 flex flex-col overflow-hidden"
              style={{ ...TEXTURED_PANEL_STYLE, maxHeight: '82vh' }}
              onClick={(e) => e.stopPropagation()}
            >
              <FourCorners color={`${ACCENT} 0.65)`} />
              <div className="px-6 pt-5 pb-2 flex items-center justify-between gap-3 shrink-0">
                <div className="arena-heading font-bold" style={{ color: TEXT_PRIMARY, fontSize: '1.125cqw' }}>
                  {t('Edit Profile')}
                </div>
                <div className="flex items-center gap-3">
                  {/* Main-menu background toggle. Writes through the
                      shared hubBackground store so the hub repaints
                      behind the overlay the moment it flips. */}
                  <button
                    type="button"
                    className="px-3 py-1.5 text-[0.625cqw] font-semibold cursor-pointer transition-colors"
                    style={{
                      background: this.state.hubBgOn ? `${ACCENT} 0.16)` : 'rgba(0,0,0,0.35)',
                      border: `1px solid ${this.state.hubBgOn ? `${ACCENT} 0.55)` : `${ACCENT} 0.25)`}`,
                      borderRadius: 'var(--radius-sm)',
                      color: this.state.hubBgOn ? ACCENT_TEXT : TEXT_MUTED,
                    }}
                    title={this.state.hubBgOn
                      ? t('Your profile background is shown as the main menu backdrop. Click to restore the default menu art')
                      : t('Show your profile background as the main menu backdrop')}
                    onClick={() => {
                      playUI(UI.CLICK);
                      const next = !this.state.hubBgOn;
                      setHubProfileBg(next);
                      this.setState({ hubBgOn: next });
                    }}
                  >
                    {this.state.hubBgOn ? t('Menu background: on') : t('Use as menu background')}
                  </button>
                  <button
                    type="button"
                    className="rounded-lg flex items-center justify-center cursor-pointer transition-colors"
                    style={{ width: '1.5cqw', height: '1.5cqw', color: TEXT_MUTED }}
                    onMouseEnter={(e) => { e.currentTarget.style.color = TEXT_BODY; e.currentTarget.style.background = `${ACCENT} 0.12)`; }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = TEXT_MUTED; e.currentTarget.style.background = 'transparent'; }}
                    onClick={() => this.setState({ editProfileOpen: false })}
                  >
                    <svg style={{ width: '0.75cqw', height: '0.75cqw' }} viewBox="0 0 14 14" fill="none">
                      <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Tab bar. Same chapter-heading recipe the showcase
                  panel uses, so the two surfaces feel like part of
                  the same family. */}
              <TabBar
                className="shrink-0 flex items-stretch border-b"
                style={{ borderColor: `${ACCENT} 0.18)`, height: '2.5cqw' }}
                variant="chapter"
                barInset="0"
                indicatorStyle={{
                  background: `radial-gradient(ellipse 70% 100% at 50% 100%, ${ACCENT_BRIGHT}0.22) 0%, ${ACCENT_BRIGHT}0.05) 55%, transparent 100%)`,
                  boxShadow: 'none',
                }}
                barStyle={{
                  height: '1.5px',
                  background: `linear-gradient(90deg, transparent 0%, ${ACCENT_TEXT} 22%, ${ACCENT_TEXT} 78%, transparent 100%)`,
                  boxShadow: `0 0 10px ${ACCENT} 0.6)`,
                }}
                activeIndex={EDIT_PROFILE_TABS.findIndex((t) => t.key === editProfileTab)}
              >
                {EDIT_PROFILE_TABS.map((tab, idx) => {
                  const isActive = editProfileTab === tab.key;
                  return (
                    <button
                      key={tab.key}
                      type="button"
                      data-tab-item
                      onClick={() => setTab(tab.key)}
                      className="flex-1 flex items-center justify-center cursor-pointer"
                      style={{
                        background: 'transparent',
                        color: isActive ? ACCENT_TEXT : TEXT_MUTED,
                        border: 'none',
                        borderLeft: idx > 0 ? `1px solid ${ACCENT} 0.14)` : 'none',
                      }}
                    >
                      <span
                        className="text-[0.6875cqw] font-bold"
                        style={{ textShadow: isActive ? `0 1px 2px rgba(0,0,0,0.65), 0 0 12px ${ACCENT} 0.45)` : '0 1px 2px rgba(0,0,0,0.6)' }}
                      >
                        {t(tab.label)}
                      </span>
                    </button>
                  );
                })}
              </TabBar>

              {/* Search row. Present on every tab. The list of crowns
                  is small enough that search is overkill, but we keep
                  it for consistency and because the catalog will grow. */}
              <div className="shrink-0 px-6 pt-3 pb-2">
                <input
                  type="text"
                  placeholder={editProfileTab === 'title' ? t('Search titles…') : t('Search cards…')}
                  value={editProfileSearch}
                  onInput={(e) => this.setState({ editProfileSearch: e.target.value })}
                  className="w-full px-3 py-2 text-[0.75cqw] outline-none"
                  style={{
                    background: 'rgba(0,0,0,0.4)',
                    border: `1px solid ${ACCENT} 0.22)`,
                    borderRadius: 'var(--radius-md)',
                    color: TEXT_BODY,
                  }}
                />
              </div>

              <div className="flex-1 overflow-y-auto px-6 pb-5">
                {editProfileTab === 'avatar' ? (
                  ownedAvatars.length === 0 ? (
                    <div className="text-[0.75cqw] italic text-center py-6" style={{ color: TEXT_MUTED }}>
                      {search ? t('No avatars match "{query}".', { query: editProfileSearch }) : t("You don't own any Avatar cards yet.")}
                    </div>
                  ) : (
                    <div className="grid grid-cols-6 gap-2">
                      {ownedAvatars.map((card) => {
                        const isSelected = profile.profileAvatar === card.unique_id;
                        const imageUrl = card.printings?.[0]?.image_url;
                        return (
                          <button
                            key={card.unique_id}
                            type="button"
                            title={card.name}
                            onClick={async () => {
                              try {
                                await this.props.onUpdateAvatar?.(card.unique_id);
                                this.setState((s) => ({
                                  profile: s.profile ? { ...s.profile, profileAvatar: card.unique_id } : s.profile,
                                }));
                              } catch (err) {
                                console.warn('[FriendProfileOverlay] Failed to set avatar:', err);
                              }
                            }}
                            className="relative overflow-hidden cursor-pointer transition-all"
                            style={{
                              aspectRatio: '63 / 88',
                              background: 'rgba(0,0,0,0.4)',
                              border: isSelected ? `1.5px solid ${ACCENT_TEXT}` : `1px solid ${ACCENT} 0.18)`,
                              borderRadius: RADIUS.card,
                              boxShadow: isSelected
                                ? `0 0 0 1px rgba(0,0,0,0.6), 0 0 14px ${ACCENT_BRIGHT}0.5)`
                                : '0 2px 6px rgba(0,0,0,0.4)',
                              padding: 0,
                            }}
                          >
                            {imageUrl ? (
                              <img src={imageUrl} alt={card.name} draggable={false} style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top' }} />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-[0.625cqw]" style={{ color: TEXT_MUTED }}>
                                {card.name?.[0] || '?'}
                              </div>
                            )}
                            {isSelected ? (
                              <div
                                className="absolute top-1 right-1 rounded-full flex items-center justify-center"
                                style={{
                                  width: '0.9cqw',
                                  height: '0.9cqw',
                                  background: ACCENT_TEXT,
                                  color: '#0e0a06',
                                  boxShadow: '0 1px 4px rgba(0,0,0,0.6)',
                                }}
                              >
                                <svg style={{ width: '0.5cqw', height: '0.5cqw' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M20 6L9 17l-5-5" />
                                </svg>
                              </div>
                            ) : null}
                          </button>
                        );
                      })}
                    </div>
                  )
                ) : null}

                {editProfileTab === 'background' ? (
                  ownedBackgrounds.length === 0 ? (
                    <div className="text-[0.75cqw] italic text-center py-6" style={{ color: TEXT_MUTED }}>
                      {search ? t('No cards match "{query}".', { query: editProfileSearch }) : t("You haven't unlocked any backgrounds yet. Open cosmetic packs in the store to add some.")}
                    </div>
                  ) : (
                    <div className="grid grid-cols-6 gap-2">
                      {/* Default. Clears the chosen background and
                          falls back to the avatar art. */}
                      <button
                        type="button"
                        title={t('Default. Use avatar art')}
                        onClick={async () => {
                          try {
                            await this.props.onUpdateBackground?.(null);
                            this.setState((s) => ({
                              profile: s.profile ? { ...s.profile, backgroundCardId: null } : s.profile,
                            }));
                          } catch (err) {
                            console.warn('[FriendProfileOverlay] Failed to clear background:', err);
                          }
                        }}
                        className="relative overflow-hidden cursor-pointer transition-all flex flex-col items-center justify-center gap-1"
                        style={{
                          aspectRatio: '63 / 88',
                          background: 'rgba(0,0,0,0.4)',
                          border: (profile.backgroundCardId || null) == null ? `1.5px solid ${ACCENT_TEXT}` : `1px dashed ${ACCENT} 0.32)`,
                          borderRadius: RADIUS.card,
                          color: ACCENT_TEXT,
                        }}
                      >
                        <svg style={{ width: '1.25cqw', height: '1.25cqw' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="9" />
                          <path d="M12 7v10M7 12h10" />
                        </svg>
                        <span className="text-[0.5625cqw] font-semibold text-center px-1">{t('Avatar Art')}</span>
                      </button>
                      {ownedBackgrounds.map((card) => {
                        const isSelected = profile.backgroundCardId === card.unique_id;
                        const imageUrl = card.printings?.[0]?.image_url;
                        return (
                          <button
                            key={card.unique_id}
                            type="button"
                            title={card.name}
                            onClick={async () => {
                              try {
                                await this.props.onUpdateBackground?.(card.unique_id);
                                this.setState((s) => ({
                                  profile: s.profile ? { ...s.profile, backgroundCardId: card.unique_id } : s.profile,
                                }));
                              } catch (err) {
                                console.warn('[FriendProfileOverlay] Failed to set background:', err);
                              }
                            }}
                            className="relative overflow-hidden cursor-pointer transition-all"
                            style={{
                              aspectRatio: '63 / 88',
                              background: 'rgba(0,0,0,0.4)',
                              border: isSelected ? `1.5px solid ${ACCENT_TEXT}` : `1px solid ${ACCENT} 0.18)`,
                              borderRadius: RADIUS.card,
                              boxShadow: isSelected
                                ? `0 0 0 1px rgba(0,0,0,0.6), 0 0 14px ${ACCENT_BRIGHT}0.5)`
                                : '0 2px 6px rgba(0,0,0,0.4)',
                              padding: 0,
                            }}
                          >
                            {imageUrl ? (
                              <img src={imageUrl} alt={card.name} draggable={false} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-[0.625cqw]" style={{ color: TEXT_MUTED }}>
                                {card.name?.[0] || '?'}
                              </div>
                            )}
                            {isSelected ? (
                              <div
                                className="absolute top-1 right-1 rounded-full flex items-center justify-center"
                                style={{
                                  width: '0.9cqw',
                                  height: '0.9cqw',
                                  background: ACCENT_TEXT,
                                  color: '#0e0a06',
                                  boxShadow: '0 1px 4px rgba(0,0,0,0.6)',
                                }}
                              >
                                <svg style={{ width: '0.5cqw', height: '0.5cqw' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M20 6L9 17l-5-5" />
                                </svg>
                              </div>
                            ) : null}
                          </button>
                        );
                      })}
                    </div>
                  )
                ) : null}

                {editProfileTab === 'title' ? (
                  (crowns.length === 0 && !profile.isFounder && !profile.isQaTester) ? (
                    <div className="text-[0.75cqw] italic text-center py-6" style={{ color: TEXT_MUTED }}>
                      {t("You haven't earned any crown titles yet. Become the top player for an avatar in the Hall of Fame to unlock one.")}
                    </div>
                  ) : (
                    <div className="flex flex-col gap-1.5">
                      {/* No Title. Explicit "show nothing under my
                          name." Writes the '__none__' sentinel so the
                          server can distinguish "auto-pick" (null)
                          from "explicitly hide" ('__none__'). The
                          row is active only when the player has
                          actually clicked it; the default (null)
                          state highlights the auto-picked crown
                          below instead, mirroring what's visible on
                          the profile page. */}
                      {(() => {
                        const isNone = profile.displayedCrownAvatarId === '__none__';
                        return (
                          <button
                            type="button"
                            onClick={async () => {
                              try {
                                await this.props.onSaveDisplayedCrown?.('__none__');
                                this.setState((s) => ({
                                  profile: s.profile ? { ...s.profile, displayedCrownAvatarId: '__none__' } : s.profile,
                                  selectedCrownIndex: -1,
                                }));
                              } catch (err) {
                                console.warn('[FriendProfileOverlay] Failed to hide title:', err);
                              }
                            }}
                            className="flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors text-left"
                            style={{
                              background: isNone ? `${ACCENT_BRIGHT} 0.12)` : 'rgba(0,0,0,0.35)',
                              border: `1px solid ${isNone ? ACCENT_TEXT : `${ACCENT} 0.18)`}`,
                              borderRadius: 'var(--radius-md)',
                            }}
                            onMouseEnter={(e) => { if (!isNone) e.currentTarget.style.background = `${ACCENT_BRIGHT} 0.08)`; }}
                            onMouseLeave={(e) => { if (!isNone) e.currentTarget.style.background = 'rgba(0,0,0,0.35)'; }}
                          >
                            <div
                              className="shrink-0 flex items-center justify-center rounded-full"
                              style={{
                                width: '2cqw',
                                height: '2cqw',
                                background: 'rgba(0,0,0,0.4)',
                                border: `1px solid ${ACCENT} 0.32)`,
                                color: TEXT_MUTED,
                              }}
                            >
                              <svg style={{ width: '0.875cqw', height: '0.875cqw' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="9" />
                                <path d="M5 19L19 5" />
                              </svg>
                            </div>
                            <div className="flex flex-col">
                              <span className="text-[0.75cqw] font-semibold" style={{ color: TEXT_PRIMARY }}>
                                {t('No Title')}
                              </span>
                              <span className="text-[0.625cqw] italic" style={{ color: TEXT_MUTED }}>
                                {t('Hide any title under your name')}
                              </span>
                            </div>
                            {isNone ? (
                              <span className="ml-auto shrink-0 text-[0.5625cqw] font-bold" style={{ color: ACCENT_TEXT }}>
                                {t('Active')}
                              </span>
                            ) : null}
                          </button>
                        );
                      })()}

                      {/* Herald of First Dawn. Exclusive founder
                          title, only rendered when the player has the
                          server-side `is_founder` flag. Picker writes
                          the FOUNDER_PICKER_SENTINEL to the same column
                          the crown picker uses, so PlayerTitle can
                          resolve it the same way. Founders without
                          crowns see this title by default already; the
                          picker entry exists so founders WITH crowns
                          can switch to it, and so all founders can
                          explicitly re-select it after picking '__none__'. */}
                      {/* Warden of the Proving Grounds. The QA
                          testers' title, gated on the server-side
                          `is_qa_tester` flag exactly like the founder
                          entry below, and listed above it because it
                          also wins the crownless default. */}
                      {profile.isQaTester ? (() => {
                        const isQaActive = profile.displayedCrownAvatarId === QA_PICKER_SENTINEL
                          || (crowns.length === 0 && (profile.displayedCrownAvatarId == null));
                        return (
                          <button
                            type="button"
                            onClick={async () => {
                              try {
                                await this.props.onSaveDisplayedCrown?.(QA_PICKER_SENTINEL);
                                this.setState((s) => ({
                                  profile: s.profile ? { ...s.profile, displayedCrownAvatarId: QA_PICKER_SENTINEL } : s.profile,
                                  selectedCrownIndex: -1,
                                }));
                              } catch (err) {
                                console.warn('[FriendProfileOverlay] Failed to set QA title:', err);
                              }
                            }}
                            className="flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors text-left"
                            style={{
                              background: isQaActive ? `${ACCENT_BRIGHT} 0.12)` : 'rgba(0,0,0,0.35)',
                              border: `1px solid ${isQaActive ? ACCENT_TEXT : `${ACCENT} 0.18)`}`,
                              borderRadius: 'var(--radius-md)',
                            }}
                            onMouseEnter={(e) => { if (!isQaActive) e.currentTarget.style.background = `${ACCENT_BRIGHT} 0.08)`; }}
                            onMouseLeave={(e) => { if (!isQaActive) e.currentTarget.style.background = 'rgba(0,0,0,0.35)'; }}
                          >
                            <div
                              className="shrink-0 flex items-center justify-center rounded-full overflow-hidden"
                              style={{
                                width: '2cqw',
                                height: '2cqw',
                                background: '#0a0805',
                                border: `1px solid ${ACCENT_TEXT}`,
                                boxShadow: `0 0 6px ${ACCENT} 0.5)`,
                              }}
                            >
                              {/* Sized by width, not height: the art is
                                  landscape, so width is the axis that
                                  binds inside the 2cqw disc. Matches
                                  AvatarCrownBadge's 0.74 × diameter. */}
                              <img
                                src="/ornaments/viking-ringerike-018.png"
                                alt=""
                                draggable={false}
                                style={{ width: '1.48cqw', height: 'auto' }}
                              />
                            </div>
                            <div className="flex flex-col min-w-0 flex-1">
                              <span className="text-[0.75cqw] font-semibold" style={{ color: TEXT_PRIMARY }}>
                                {QA_TITLE_LABEL}
                              </span>
                              <span className="text-[0.625cqw] italic" style={{ color: TEXT_MUTED }}>
                                {t('For the QA testers. Shown as "{title}" under your name.', { title: QA_TITLE_EPITHET })}
                              </span>
                            </div>
                            {isQaActive ? (
                              <span className="ml-auto shrink-0 text-[0.5625cqw] font-bold" style={{ color: ACCENT_TEXT }}>
                                {t('Active')}
                              </span>
                            ) : null}
                          </button>
                        );
                      })() : null}

                      {profile.isFounder ? (() => {
                        const isFounderActive = profile.displayedCrownAvatarId === FOUNDER_PICKER_SENTINEL
                          || (crowns.length === 0 && !profile.isQaTester && (profile.displayedCrownAvatarId == null));
                        return (
                          <button
                            type="button"
                            onClick={async () => {
                              try {
                                await this.props.onSaveDisplayedCrown?.(FOUNDER_PICKER_SENTINEL);
                                this.setState((s) => ({
                                  profile: s.profile ? { ...s.profile, displayedCrownAvatarId: FOUNDER_PICKER_SENTINEL } : s.profile,
                                  selectedCrownIndex: -1,
                                }));
                              } catch (err) {
                                console.warn('[FriendProfileOverlay] Failed to set founder title:', err);
                              }
                            }}
                            className="flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors text-left"
                            style={{
                              background: isFounderActive ? `${ACCENT_BRIGHT} 0.12)` : 'rgba(0,0,0,0.35)',
                              border: `1px solid ${isFounderActive ? ACCENT_TEXT : `${ACCENT} 0.18)`}`,
                              borderRadius: 'var(--radius-md)',
                            }}
                            onMouseEnter={(e) => { if (!isFounderActive) e.currentTarget.style.background = `${ACCENT_BRIGHT} 0.08)`; }}
                            onMouseLeave={(e) => { if (!isFounderActive) e.currentTarget.style.background = 'rgba(0,0,0,0.35)'; }}
                          >
                            <div
                              className="shrink-0 flex items-center justify-center rounded-full"
                              style={{
                                width: '2cqw',
                                height: '2cqw',
                                background: `radial-gradient(circle at 30% 30%, ${ACCENT_BRIGHT}0.55), rgba(60,40,15,0.85))`,
                                border: `1px solid ${ACCENT_TEXT}`,
                                boxShadow: `0 0 6px ${ACCENT} 0.5)`,
                                color: '#0e0a06',
                              }}
                            >
                              {/* Rising sun glyph. Three rays over a
                                  half-disc, evoking first light. */}
                              <svg style={{ width: '1.1cqw', height: '1.1cqw' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M3 18h18" />
                                <path d="M6 18a6 6 0 0112 0" />
                                <path d="M12 4v3" />
                                <path d="M4.5 8.5l2 2" />
                                <path d="M19.5 8.5l-2 2" />
                              </svg>
                            </div>
                            <div className="flex flex-col min-w-0 flex-1">
                              <span className="text-[0.75cqw] font-semibold" style={{ color: TEXT_PRIMARY }}>
                                {FOUNDER_TITLE_LABEL}
                              </span>
                              <span className="text-[0.625cqw] italic" style={{ color: TEXT_MUTED }}>
                                {t('For original beta testers. Thank you for being here from the start.')}
                              </span>
                            </div>
                            {isFounderActive ? (
                              <span className="ml-auto shrink-0 text-[0.5625cqw] font-bold" style={{ color: ACCENT_TEXT }}>
                                {t('Active')}
                              </span>
                            ) : null}
                          </button>
                        );
                      })() : null}

                      {titleHits.length === 0 ? (
                        <div className="text-[0.75cqw] italic text-center py-3" style={{ color: TEXT_MUTED }}>
                          {t('No titles match "{query}".', { query: editProfileSearch })}
                        </div>
                      ) : null}

                      {titleHits.map((crown) => {
                        const isExplicit = explicitCrownId === crown.avatarId;
                        const isAuto = !!autoCrown && autoCrown.avatarId === crown.avatarId;
                        const isSelected = isExplicit || isAuto;
                        return (
                          <button
                            key={crown.avatarId}
                            type="button"
                            onClick={async () => {
                              try {
                                await this.props.onSaveDisplayedCrown?.(crown.avatarId);
                                this.setState((s) => ({
                                  profile: s.profile ? { ...s.profile, displayedCrownAvatarId: crown.avatarId } : s.profile,
                                  selectedCrownIndex: -1,
                                }));
                              } catch (err) {
                                console.warn('[FriendProfileOverlay] Failed to set title:', err);
                              }
                            }}
                            className="flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors text-left"
                            style={{
                              background: isSelected ? `${ACCENT_BRIGHT} 0.12)` : 'rgba(0,0,0,0.35)',
                              border: `1px solid ${isSelected ? ACCENT_TEXT : `${ACCENT} 0.18)`}`,
                              borderRadius: 'var(--radius-md)',
                            }}
                            onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = `${ACCENT_BRIGHT} 0.08)`; }}
                            onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = 'rgba(0,0,0,0.35)'; }}
                          >
                            <AvatarCrownBadge crown={crown} size="2cqw" active={isSelected} showTooltip={false} />
                            <div className="flex flex-col min-w-0 flex-1">
                              <span className="text-[0.75cqw] font-semibold" style={{ color: TEXT_PRIMARY }}>
                                {crown.label}
                              </span>
                              {crown.title ? (
                                <span className="text-[0.625cqw] italic truncate" style={{ color: ACCENT_TEXT, opacity: 0.85 }}>
                                  {crown.title}
                                </span>
                              ) : null}
                              <span className="text-[0.5625cqw] tabular-nums" style={{ color: TEXT_MUTED }}>
                                {crown.wins === 1 ? t('1 win') : t('{n} wins', { n: crown.wins })}
                              </span>
                            </div>
                            {isSelected ? (
                              <span className="shrink-0 text-[0.5625cqw] font-bold" style={{ color: ACCENT_TEXT }}>
                                {isAuto && !isExplicit ? t('Active (Auto)') : t('Active')}
                              </span>
                            ) : null}
                          </button>
                        );
                      })}
                    </div>
                  )
                ) : null}
              </div>
            </div>
          </div>
          );
        })() : null}

        {/* Card inspector for the showcase. Opened by hovering a card
            and pressing Space, or by left-clicking a card. Same UX
            the deck builder uses, so the muscle memory transfers. */}
        {this.state.inspectedShowcaseEntry ? (
          <CardInspector
            card={this.state.inspectedShowcaseEntry.card}
            imageUrl={this.state.inspectedShowcaseEntry.printing?.image_url}
            rarity={this.state.inspectedShowcaseEntry.card?.rarity}
            foiling={this.state.inspectedShowcaseEntry.printing?.foiling}
            onClose={() => this.setState({ inspectedShowcaseEntry: null })}
          />
        ) : null}

        {/* Single-slot replacement modal. Self only. Triggered by
            clicking either a filled showcase card (in edit mode) or
            an empty "+ Add Card" tile. Picks ONE card from the
            player's collection and writes it into that specific slot
            on click. No staging, no save button, no multi-select. */}
        {replacingSlotIndex != null ? (
          <div
            className="absolute inset-0 z-40 flex items-center justify-center"
            style={{ background: 'radial-gradient(ellipse 80% 70% at 50% 50%, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.94) 100%)' }}
            onClick={this.closeReplaceSlot}
          >
            <div
              className="w-full max-w-[48cqw] mx-4 flex flex-col overflow-hidden"
              style={{ ...TEXTURED_PANEL_STYLE, maxHeight: '80vh' }}
              onClick={(e) => e.stopPropagation()}
            >
              <FourCorners color={`${ACCENT} 0.65)`} />
              <div className="px-6 pt-5 pb-3 flex items-center justify-between gap-3">
                <div>
                  <div className="arena-heading font-bold" style={{ color: TEXT_PRIMARY, fontSize: '1.125cqw' }}>
                    {visibleShowcase[replacingSlotIndex] ? t('Replace Card') : t('Add Card')}
                  </div>
                  <div className="text-[0.6875cqw] mt-1" style={{ color: TEXT_MUTED }}>
                    {visibleShowcase[replacingSlotIndex]
                      ? t('Pick a card to replace {name}.', { name: visibleShowcase[replacingSlotIndex].name })
                      : t('Pick a card from your collection to pin to this slot.')}
                  </div>
                </div>
                <input
                  type="text"
                  placeholder={t('Search…')}
                  value={showcasePickerSearch}
                  onInput={(e) => this.setState({ showcasePickerSearch: e.target.value })}
                  className="px-3 py-2 text-[0.75cqw] outline-none"
                  style={{
                    background: 'rgba(0,0,0,0.4)',
                    border: `1px solid ${ACCENT} 0.25)`,
                    borderRadius: 'var(--radius-md)',
                    color: TEXT_BODY,
                    minWidth: '12cqw',
                  }}
                />
              </div>
              <div className="flex-1 overflow-y-auto px-6 pb-4">
                {(() => {
                  const search = showcasePickerSearch.trim().toLowerCase();
                  // Collapse the player's collection to one entry per
                  // cardId. Pinning is conceptually about the card,
                  // not the printing. We pick the first foiling we
                  // see for each cardId.
                  const ownedByCardId = new Map();
                  for (const entry of (profile.collection || [])) {
                    if (!ownedByCardId.has(entry.cardId)) {
                      ownedByCardId.set(entry.cardId, entry.foiling || 'S');
                    }
                  }
                  // Cards already pinned in OTHER slots. Disable
                  // them so the player can't pin the same card twice.
                  const pinnedElsewhere = new Set(
                    (profile.showcaseCards || [])
                      .map((c, idx) => (idx !== replacingSlotIndex && c?.cardId ? c.cardId : null))
                      .filter(Boolean)
                  );
                  const cards = [];
                  for (const [cardId, foiling] of ownedByCardId) {
                    const meta = cardById.get(cardId);
                    if (!meta) continue;
                    if (search && !(meta.name || '').toLowerCase().includes(search)) continue;
                    cards.push({
                      cardId,
                      foiling,
                      name: meta.name || cardId,
                      imageUrl: meta.printings?.[0]?.image_url || null,
                      alreadyPinned: pinnedElsewhere.has(cardId),
                    });
                  }
                  cards.sort((a, b) => a.name.localeCompare(b.name));
                  if (cards.length === 0) {
                    return (
                      <div className="text-center py-12 text-[0.75cqw] italic" style={{ color: TEXT_MUTED }}>
                        {search ? t('No cards match "{query}"', { query: showcasePickerSearch }) : t("You don't own any cards yet.")}
                      </div>
                    );
                  }
                  return (
                    <div className="grid grid-cols-6 gap-2 auto-rows-min">
                      {cards.map((card) => (
                        <button
                          key={card.cardId}
                          type="button"
                          title={card.alreadyPinned ? t('{name} (already in showcase)', { name: card.name }) : card.name}
                          disabled={card.alreadyPinned}
                          onClick={() => this.applySlotReplacement(replacingSlotIndex, card)}
                          className="relative overflow-hidden cursor-pointer transition-all"
                          style={{
                            aspectRatio: '63 / 88',
                            background: 'rgba(0,0,0,0.4)',
                            border: `1px solid ${ACCENT} 0.18)`,
                            borderRadius: RADIUS.card,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
                            opacity: card.alreadyPinned ? 0.35 : 1,
                            padding: 0,
                            cursor: card.alreadyPinned ? 'not-allowed' : 'pointer',
                          }}
                          onMouseEnter={(e) => {
                            if (!card.alreadyPinned) {
                              e.currentTarget.style.borderColor = ACCENT_TEXT;
                              e.currentTarget.style.boxShadow = `0 0 0 1px rgba(0,0,0,0.6), 0 0 14px ${ACCENT_BRIGHT}0.45)`;
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!card.alreadyPinned) {
                              e.currentTarget.style.borderColor = `${ACCENT} 0.18)`;
                              e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.4)';
                            }
                          }}
                        >
                          {card.imageUrl ? (
                            <img src={card.imageUrl} alt={card.name} draggable={false} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-[0.625cqw]" style={{ color: TEXT_MUTED }}>
                              {card.name[0]}
                            </div>
                          )}
                          {card.alreadyPinned ? (
                            <div
                              className="absolute inset-0 flex items-end justify-center pb-1"
                              style={{ background: 'linear-gradient(180deg, transparent 60%, rgba(0,0,0,0.85) 100%)' }}
                            >
                              <span className="text-[0.5cqw] font-bold" style={{ color: ACCENT_TEXT }}>
                                {t('Pinned')}
                              </span>
                            </div>
                          ) : null}
                        </button>
                      ))}
                    </div>
                  );
                })()}
              </div>
              <div className="px-6 py-3 flex items-center justify-between gap-2.5" style={{ borderTop: `1px solid ${ACCENT} 0.25)` }}>
                {/* Remove. Only meaningful if the slot was filled. */}
                {visibleShowcase[replacingSlotIndex] ? (
                  <button
                    type="button"
                    className="px-4 py-2 text-[0.6875cqw] cursor-pointer transition-all"
                    style={{ ...DANGER_BTN, borderRadius: 'var(--radius-md)' }}
                    onClick={() => this.applySlotReplacement(replacingSlotIndex, null)}
                  >
                    {t('Remove from showcase')}
                  </button>
                ) : <span />}
                <button
                  type="button"
                  className="px-4 py-2 text-[0.6875cqw] cursor-pointer transition-all"
                  style={{ ...BEVELED_BTN, backgroundColor: 'rgb(20, 14, 6)', color: TEXT_BODY, borderRadius: 'var(--radius-md)' }}
                  onClick={this.closeReplaceSlot}
                >
                  {t('Cancel')}
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {/* Badges modal. Read-only viewer triggered by clicking the
            header badge strip. Designed as a generic badge gallery so
            future non-crown badge types (achievements, event rewards)
            can be appended without restructuring the strip again. The
            grid is scrollable when the player has more badges than fit
            in one viewport. Selection happens elsewhere. Clicking a
            badge here does not change the displayed title; only the
            Edit Profile → Title tab can do that. */}
        {showBadgesModal ? (
          <div
            className="absolute inset-0 z-40 flex items-center justify-center"
            style={{ background: 'radial-gradient(ellipse 80% 70% at 50% 50%, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.94) 100%)' }}
            onClick={() => this.setState({ showBadgesModal: false })}
          >
            {/* Panel is a fixed-size frame so the modal layout doesn't
                jump between a player with 1 badge and one with 30.
                Width fits a 3-column grid; height fits exactly 3 rows
                of cells (≈9 badges) before the inner area becomes
                scrollable. */}
            <div
              className="flex flex-col overflow-hidden"
              style={{ ...TEXTURED_PANEL_STYLE, width: '54cqw', height: '48cqw' }}
              onClick={(e) => e.stopPropagation()}
            >
              <FourCorners color={`${ACCENT} 0.65)`} />
              <div className="relative px-6 pt-6 pb-2 shrink-0">
                <button
                  type="button"
                  className="absolute top-3 right-3 rounded-lg flex items-center justify-center cursor-pointer transition-colors"
                  style={{ width: '1.5cqw', height: '1.5cqw', color: TEXT_MUTED }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = TEXT_BODY; e.currentTarget.style.background = `${ACCENT} 0.12)`; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = TEXT_MUTED; e.currentTarget.style.background = 'transparent'; }}
                  onClick={() => this.setState({ showBadgesModal: false })}
                  aria-label={t('Close')}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '0.875cqw', height: '0.875cqw' }}>
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
                <div className="flex flex-col items-center leading-tight gap-1">
                  <span
                    className="arena-heading font-bold"
                    style={{
                      color: ACCENT_TEXT,
                      fontSize: '1.25cqw',
                      textShadow: `0 2px 4px rgba(0,0,0,0.7), 0 0 14px ${ACCENT_BRIGHT} 0.35)`,
                    }}
                  >
                    {t('Badges')}
                  </span>
                  <span className="text-[0.6875cqw] italic" style={{ color: TEXT_MUTED }}>
                    {t('{n} earned', { n: allBadges.length })}
                  </span>
                </div>
              </div>
              <OrnamentalDivider />
              <div className="px-6 py-4 overflow-y-auto flex-1 min-h-0">
                {allBadges.length === 0 ? (
                  <div className="text-center py-8 text-[0.8125cqw]" style={{ color: TEXT_MUTED }}>
                    {t('No badges earned yet.')}
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-5">
                    {allBadges.map((badge) => {
                      const isActive = chosenCrown?.avatarId === badge.id;
                      const isPinned = pinnedIds.has(badge.id);
                      // Role badges (founder, QA) describe how they
                      // were earned; crowns show a win count instead.
                      const isRole = badge.kind === 'founder' || badge.kind === 'qa';
                      return (
                        <div
                          key={badge.id}
                          className="relative flex flex-col items-center text-center px-4 pt-4 pb-3 rounded-lg"
                          style={{
                            background: isPinned ? `${ACCENT} 0.10)` : 'rgba(20,14,6,0.55)',
                            border: `1px solid ${isPinned ? `${ACCENT} 0.5)` : `${ACCENT} 0.15)`}`,
                          }}
                        >
                          {isSelf ? (
                            <button
                              type="button"
                              title={isPinned ? t('Unpin from profile') : t('Pin to profile')}
                              aria-label={isPinned ? t('Unpin badge') : t('Pin badge')}
                              onClick={() => togglePinBadge(badge.id)}
                              className="absolute top-1.5 right-1.5 cursor-pointer transition-colors flex items-center justify-center"
                              style={{
                                width: '1.6cqw',
                                height: '1.6cqw',
                                borderRadius: 'var(--radius-sm)',
                                background: isPinned ? `${ACCENT} 0.22)` : 'rgba(0,0,0,0.35)',
                                border: `1px solid ${isPinned ? `${ACCENT} 0.55)` : `${ACCENT} 0.22)`}`,
                                color: isPinned ? ACCENT_TEXT : TEXT_MUTED,
                                fontSize: '0.875cqw',
                                lineHeight: 1,
                              }}
                            >
                              {isPinned ? '★' : '☆'}
                            </button>
                          ) : null}
                          <AvatarCrownBadge crown={badge} size="4.5cqw" active={isActive} showTooltip={false} />
                          <div
                            className="mt-3 text-[0.875cqw] font-semibold arena-heading"
                            style={{ color: ACCENT_TEXT }}
                          >
                            {badge.label}
                          </div>
                          {badge.title && badge.title !== badge.label ? (
                            <div className="text-[0.75cqw] italic mt-1" style={{ color: TEXT_BODY }}>
                              {badge.title}
                            </div>
                          ) : null}
                          {isRole ? (
                            <div className="text-[0.6875cqw] italic mt-1.5 leading-snug" style={{ color: TEXT_MUTED }}>
                              {badge.description}
                            </div>
                          ) : (
                            <div className="text-[0.6875cqw] mt-1.5 tabular-nums" style={{ color: TEXT_MUTED }}>
                              {badge.wins === 1 ? t('1 win') : t('{n} wins', { n: badge.wins })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              {isSelf ? (
                <div className="px-6 pb-3 pt-2 shrink-0 text-center text-[0.625cqw]" style={{ color: TEXT_MUTED }}>
                  {t('Tap the ★ on a badge to pin or unpin it (up to {n}).', { n: PIN_LIMIT })}
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    );
  }
}
